import React, { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Upload,
  Trash2,
  FileText,
  Lock,
  Eye,
  EyeOff,
  X,
  Download,
  CreditCard,
  ShoppingBag,
  Utensils,
  Heart,
  Plane,
  Fuel,
  Tv,
  Globe,
  MoreHorizontal,
  ShoppingCart,
  CalendarDays,
  LayoutDashboard,
  Search,
  Pencil,
  Plus,
  Droplets,
  Zap,
  Wifi,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Star,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LabelList,
} from "recharts";
import { apiFetch } from "../../lib/api";
import { useConfigStore } from "../../store/configStore";
import toast from "react-hot-toast";
import Button, { buttonClass } from "../ui/Button";
import ActionMenu from "../ui/ActionMenu";
import { pageContainerVariants, pageItemVariants } from "../../constants/motionVariants";

// ── Types ─────────────────────────────────────────────────────────────────────
interface Transaction {
  date: string;
  merchant: string;
  amount: number;
  isCredit: boolean;
  category: string;
  cardType?: string;
  cardLast4?: string;
}

interface CategorySummary {
  [cat: string]: { amount: number; count: number };
}

interface ParseResult {
  bank: string;
  bankRecognized?: boolean;
  period: string;
  statement_month?: string;
  transactions: Transaction[];
  summary: CategorySummary;
  points_by_card?: Record<string, number>;
  points_expiry_by_card?: Record<
    string,
    { expiring: number; expiry_date: string }
  >;
  earned_points_by_card?: Record<string, number>;
  card_sections?: Record<string, string>;
  card_meta?: { cardType: string; cardLast4: string }[];
  cut_day?: number | null;
  due_day?: number | null;
}

interface StatementRecord {
  id: number;
  bank: string;
  card_type: string;
  card_last4: string;
  statement_month: string; // YYYY-MM
  total_spent: number;
  total_credit: number;
  txn_count: number;
  category_summary: CategorySummary;
  total_points: number;
  earned_points?: number;
  expiring_points?: number;
  expiry_date?: string;
  pdf_path?: string | null;
}

interface TopMerchant {
  merchant: string;
  total: number;
  count: number;
  category: string;
}

interface DueDateEntry {
  card_name: string;
  cut_day: number;
  due_day: number;
}
type DueDateMap = Record<string, DueDateEntry>;

function dueDaysUntil(due_day: number): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const y = today.getFullYear(),
    mo = today.getMonth();
  let next = new Date(y, mo, due_day);
  if (next <= today) next = new Date(y, mo + 1, due_day);
  return Math.round((next.getTime() - today.getTime()) / 86400000);
}
function dueDateLabel(due_day: number): string {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const y = today.getFullYear(),
    mo = today.getMonth();
  let next = new Date(y, mo, due_day);
  if (next <= today) next = new Date(y, mo + 1, due_day);
  return `${next.getDate()}/${next.getMonth() + 1}`;
}

// ── Constants ─────────────────────────────────────────────────────────────────
const CATEGORY_CONFIG: Record<
  string,
  { icon: React.ReactNode; color: string; gradient: string }
> = {
  ชอปปิ้ง: {
    icon: <ShoppingBag className="w-4 h-4" />,
    color: "#f59e0b",
    gradient: "from-amber-400 to-orange-500",
  },
  ร้านอาหาร: {
    icon: <Utensils className="w-4 h-4" />,
    color: "#ef4444",
    gradient: "from-red-400 to-rose-500",
  },
  สุขภาพ: {
    icon: <Heart className="w-4 h-4" />,
    color: "#22c55e",
    gradient: "from-green-400 to-emerald-500",
  },
  เดินทาง: {
    icon: <Plane className="w-4 h-4" />,
    color: "#3b82f6",
    gradient: "from-blue-400 to-indigo-500",
  },
  น้ำมัน: {
    icon: <Fuel className="w-4 h-4" />,
    color: "#64748b",
    gradient: "from-slate-400 to-slate-600",
  },
  บันเทิง: {
    icon: <Tv className="w-4 h-4" />,
    color: "#a855f7",
    gradient: "from-purple-400 to-violet-500",
  },
  "ค่า Subscription": {
    icon: <Star className="w-4 h-4" />,
    color: "#f59e0b",
    gradient: "from-amber-400 to-orange-500",
  },
  "e-Commerce": {
    icon: <Globe className="w-4 h-4" />,
    color: "#06b6d4",
    gradient: "from-cyan-400 to-sky-500",
  },
  ซุปเปอร์มาเก็ต: {
    icon: <ShoppingCart className="w-4 h-4" />,
    color: "#10b981",
    gradient: "from-emerald-400 to-teal-500",
  },
  ค่าน้ำ: {
    icon: <Droplets className="w-4 h-4" />,
    color: "#38bdf8",
    gradient: "from-sky-300 to-blue-400",
  },
  ค่าไฟ: {
    icon: <Zap className="w-4 h-4" />,
    color: "#facc15",
    gradient: "from-yellow-300 to-amber-400",
  },
  ค่าเน็ต: {
    icon: <Wifi className="w-4 h-4" />,
    color: "#818cf8",
    gradient: "from-indigo-300 to-violet-400",
  },
  อื่นๆ: {
    icon: <MoreHorizontal className="w-4 h-4" />,
    color: "#94a3b8",
    gradient: "from-gray-400 to-slate-500",
  },
};

const PIE_COLORS = Object.values(CATEGORY_CONFIG).map((c) => c.color);

function fmt(n: number) {
  return n.toLocaleString("th-TH", { minimumFractionDigits: 2 });
}

// ── Due Date Section ──────────────────────────────────────────────────────────
interface DueDateSectionProps {
  cards: { last4: string; bank: string; cardType: string }[];
  profiles: CardProfiles;
}

const DueDateSection: React.FC<DueDateSectionProps> = ({ cards, profiles }) => {
  const { getConfigValue, updateConfig, fetchConfig } = useConfigStore();
  const rawValue = getConfigValue("credit_due_dates", "{}");
  let dueDates: DueDateMap = {};
  try {
    dueDates = JSON.parse(rawValue);
  } catch {}

  const [editKey, setEditKey] = React.useState<string | null>(null);
  const [form, setForm] = React.useState<{
    card_name: string;
    cut_day: number;
    due_day: number;
  }>({ card_name: "", cut_day: 25, due_day: 15 });
  const [saving, setSaving] = React.useState(false);
  const [showAdd, setShowAdd] = React.useState(false);
  const [newKey, setNewKey] = React.useState("");

  React.useEffect(() => {
    fetchConfig("credit_due_dates");
  }, []);

  const openEdit = (key: string, entry: DueDateEntry) => {
    setEditKey(editKey === key ? null : key);
    setForm({
      card_name: entry.card_name,
      cut_day: entry.cut_day,
      due_day: entry.due_day,
    });
  };

  const saveEdit = async () => {
    if (!editKey) return;
    setSaving(true);
    try {
      const updated = { ...dueDates, [editKey]: form };
      await updateConfig(
        "credit_due_dates",
        JSON.stringify(updated),
        "Credit card due dates",
      );
      setEditKey(null);
    } catch {
      toast.error("บันทึกไม่สำเร็จ");
    } finally {
      setSaving(false);
    }
  };

  const removeEntry = async (key: string) => {
    const updated = { ...dueDates };
    delete updated[key];
    await updateConfig(
      "credit_due_dates",
      JSON.stringify(updated),
      "Credit card due dates",
    );
    if (editKey === key) setEditKey(null);
  };

  const saveNew = async () => {
    if (!newKey || newKey.length !== 4) return;
    const updated = { ...dueDates, [newKey]: form };
    await updateConfig(
      "credit_due_dates",
      JSON.stringify(updated),
      "Credit card due dates",
    );
    setShowAdd(false);
    setNewKey("");
    toast.success("เพิ่มบัตรแล้ว");
  };

  const entries = Object.entries(dueDates);
  const unconfigured = cards.filter((c) => !dueDates[c.last4]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-primary-500" />
          วันครบกำหนดชำระ
        </h3>
        <button
          onClick={() => {
            setShowAdd(true);
            setNewKey("");
            setForm({ card_name: "", cut_day: 25, due_day: 15 });
            setEditKey(null);
          }}
          className="flex items-center gap-1 text-xs text-primary-500 hover:text-primary-700 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> เพิ่มบัตร
        </button>
      </div>

      {entries.length === 0 && !showAdd && (
        <p className="text-xs text-gray-400 dark:text-gray-400 text-center py-3">
          ยังไม่มีบัตรที่ตั้งค่าวันชำระ — กด{" "}
          <span className="text-primary-500">เพิ่มบัตร</span> เพื่อเริ่ม
        </p>
      )}

      {/* Card chips */}
      {entries.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {entries.map(([last4, cfg]) => {
            const days = dueDaysUntil(cfg.due_day);
            const isEditing = editKey === last4;
            const urgency =
              days === 0
                ? "today"
                : days <= 3
                  ? "urgent"
                  : days <= 7
                    ? "warning"
                    : "ok";
            const img = cardImage(undefined, undefined, last4, profiles);

            const borderStyle = {
              today:
                "border-red-400 dark:border-red-600 bg-red-50 dark:bg-red-900/20",
              urgent:
                "border-red-300 dark:border-red-700 bg-red-50/60 dark:bg-red-900/10",
              warning:
                "border-orange-300 dark:border-orange-700 bg-orange-50/60 dark:bg-orange-900/10",
              ok: "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50",
            }[urgency];

            return (
              <div
                key={last4}
                className={`rounded-2xl border p-4 transition-all min-w-[185px] ${borderStyle} ${isEditing ? "ring-2 ring-primary-400 dark:ring-primary-500" : ""}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    {img ? (
                      <img
                        src={img}
                        alt={cfg.card_name}
                        className="h-5 w-auto rounded object-contain"
                      />
                    ) : (
                      <CreditCard className="w-4 h-4 text-gray-400" />
                    )}
                    <div>
                      <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 leading-tight">
                        {cfg.card_name || `บัตร ···${last4}`}
                      </p>
                      <p className="text-[10px] font-mono text-gray-400">
                        ···{last4}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => openEdit(last4, cfg)}
                    className="text-gray-300 dark:text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors flex-shrink-0"
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                </div>

                <div className="mt-3 flex items-center gap-3">
                  <div className="text-center">
                    <p className="text-[9px] font-bold uppercase text-gray-400 tracking-wider">
                      ตัดบัญชี
                    </p>
                    <p className="text-sm font-bold text-gray-700 dark:text-gray-300">
                      {cfg.cut_day}
                    </p>
                  </div>
                  <div className="w-px h-7 bg-gray-200 dark:bg-gray-600" />
                  <div className="text-center">
                    <p className="text-[9px] font-bold uppercase text-gray-400 tracking-wider">
                      วันชำระ
                    </p>
                    <p className="text-sm font-bold text-gray-700 dark:text-gray-300">
                      {dueDateLabel(cfg.due_day)}
                    </p>
                  </div>
                  <div className="ml-auto text-right">
                    <p className="text-[9px] font-bold uppercase text-gray-400 tracking-wider">
                      เหลือ
                    </p>
                    <p
                      className={`text-lg font-black leading-none ${
                        urgency === "today"
                          ? "text-loss animate-pulse"
                          : urgency === "urgent"
                            ? "text-loss"
                            : urgency === "warning"
                              ? "text-orange-500"
                              : "text-primary-500"
                      }`}
                    >
                      {days === 0 ? "วันนี้!" : `${days}d`}
                    </p>
                  </div>
                </div>

                {isEditing && (
                  <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 space-y-2">
                    <input
                      type="text"
                      value={form.card_name}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, card_name: e.target.value }))
                      }
                      placeholder="ชื่อบัตร"
                      className="w-full px-3 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <label className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">
                          วันตัดบัญชี
                        </label>
                        <input
                          type="number"
                          min={1}
                          max={31}
                          value={form.cut_day}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              cut_day: Math.min(
                                31,
                                Math.max(1, +e.target.value),
                              ),
                            }))
                          }
                          className="w-full px-2 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-center font-bold focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">
                          วันครบกำหนด
                        </label>
                        <input
                          type="number"
                          min={1}
                          max={31}
                          value={form.due_day}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              due_day: Math.min(
                                31,
                                Math.max(1, +e.target.value),
                              ),
                            }))
                          }
                          className="w-full px-2 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-center font-bold focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                    </div>
                    <div className="flex gap-1.5">
                      <button
                        onClick={saveEdit}
                        disabled={saving}
                        className={buttonClass('primary', 'sm', 'flex-1')}
                      >
                        {saving ? "..." : "บันทึก"}
                      </button>
                      <button
                        onClick={() => removeEntry(last4)}
                        className={buttonClass('ghost', 'sm')}
                      >
                        ลบ
                      </button>
                      <button
                        onClick={() => setEditKey(null)}
                        className={buttonClass('ghost', 'sm')}
                      >
                        ยกเลิก
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add new card form */}
      {showAdd && (
        <div className="rounded-2xl border border-dashed border-primary-300 dark:border-primary-700 bg-primary-50/30 dark:bg-primary-900/10 p-4 space-y-2.5">
          <p className="text-xs font-semibold text-primary-600 dark:text-primary-400 flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> เพิ่มบัตรใหม่
          </p>
          {unconfigured.length > 0 && (
            <select
              value={newKey}
              onChange={(e) => {
                setNewKey(e.target.value);
                const c = unconfigured.find((x) => x.last4 === e.target.value);
                if (c)
                  setForm((f) => ({
                    ...f,
                    card_name: cardDisplayName(
                      c.cardType || undefined,
                      c.bank,
                      c.last4,
                      profiles,
                    ),
                  }));
              }}
              className="w-full px-3 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">— เลือกจากบัตรที่มีประวัติ —</option>
              {unconfigured.map((c) => (
                <option key={c.last4} value={c.last4}>
                  {cardDisplayName(
                    c.cardType || undefined,
                    c.bank,
                    c.last4,
                    profiles,
                  )}{" "}
                  ···{c.last4}
                </option>
              ))}
            </select>
          )}
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">
                เลข 4 หลักสุดท้าย
              </label>
              <input
                type="text"
                maxLength={4}
                value={newKey}
                onChange={(e) =>
                  setNewKey(e.target.value.replace(/\D/g, "").slice(0, 4))
                }
                placeholder="xxxx"
                className="w-full px-2 py-1.5 text-xs font-mono rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-center focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="flex-1">
              <label className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">
                ชื่อบัตร
              </label>
              <input
                type="text"
                value={form.card_name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, card_name: e.target.value }))
                }
                placeholder="ชื่อบัตร"
                className="w-full px-2 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">
                วันตัดบัญชี (1–31)
              </label>
              <input
                type="number"
                min={1}
                max={31}
                value={form.cut_day}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    cut_day: Math.min(31, Math.max(1, +e.target.value)),
                  }))
                }
                className="w-full px-2 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-center font-bold focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="flex-1">
              <label className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">
                วันครบกำหนด (1–31)
              </label>
              <input
                type="number"
                min={1}
                max={31}
                value={form.due_day}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    due_day: Math.min(31, Math.max(1, +e.target.value)),
                  }))
                }
                className="w-full px-2 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-center font-bold focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
          <div className="flex gap-1.5">
            <button
              onClick={saveNew}
              disabled={!newKey || newKey.length !== 4}
              className={buttonClass('primary', 'sm', 'flex-1')}
            >
              บันทึก
            </button>
            <button
              onClick={() => {
                setShowAdd(false);
                setNewKey("");
              }}
              className={buttonClass('ghost', 'sm')}
            >
              ยกเลิก
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Upload Zone ───────────────────────────────────────────────────────────────
interface UploadZoneProps {
  onResult: (r: ParseResult, file: File, passwords: string) => void | Promise<void>;
  onStartAnalyzing?: () => void;
}

const UploadZone: React.FC<UploadZoneProps> = ({
  onResult,
  onStartAnalyzing,
}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [passwords, setPasswords] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [needPassword, setNeedPassword] = useState(false);

  const handleFile = (f: File) => {
    if (f.type !== "application/pdf") {
      toast.error("รองรับเฉพาะไฟล์ PDF");
      return;
    }
    if (f.size > 20 * 1024 * 1024) {
      toast.error("ไฟล์ใหญ่เกิน 20MB");
      return;
    }
    setFile(f);
    setNeedPassword(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const submit = async (extraPassword?: string) => {
    if (!file) return;
    setLoading(true);
    onStartAnalyzing?.();
    try {
      const form = new FormData();
      form.append("file", file);
      const pwds = [passwords, extraPassword].filter(Boolean).join(",");
      if (pwds) form.append("passwords", pwds);

      const res = await apiFetch("/api/parse-credit-pdf", {
        method: "POST",
        body: form,
      });
      const data = await res.json();

      if (!res.ok) {
        if (data.error === "password_required") {
          setNeedPassword(true);
          toast.error("PDF ติด password — กรุณาใส่ password");
          return;
        }
        toast.error(data.error || "เกิดข้อผิดพลาด");
        return;
      }

      if (data.transactions.length === 0 && !data.bankRecognized) {
        toast.error("ไม่พบรายการใช้จ่าย อาจเป็น format ที่ยังไม่รองรับ");
        return;
      }

      onResult(data, file, pwds);
    } catch (e) {
      toast.error("เกิดข้อผิดพลาดในการประมวลผล");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto space-y-4">
      {/* Drop zone */}
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        onClick={() => fileRef.current?.click()}
        className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-2xl p-10 text-center cursor-pointer hover:border-primary-400 dark:hover:border-primary-500 transition-colors h-[160px] flex items-center justify-center"
      >
        <input
          ref={fileRef}
          type="file"
          accept="application/pdf"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleFile(f);
          }}
        />
        {file ? (
          <div className="flex items-center justify-center gap-3">
            <FileText className="w-8 h-8 text-primary-500" />
            <div className="text-left">
              <p className="font-medium text-gray-800 dark:text-gray-200">
                {file.name}
              </p>
              <p className="text-sm text-gray-500">
                {(file.size / 1024).toFixed(0)} KB
              </p>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setFile(null);
                setNeedPassword(false);
              }}
              className="ml-2 text-gray-400 hover:text-red-500"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <>
            <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
            <p className="font-medium text-gray-700 dark:text-gray-300">
              วางไฟล์ PDF ที่นี่ หรือคลิกเพื่อเลือก
            </p>
          </>
        )}
      </div>

      {/* Password field */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          <Lock className="w-3.5 h-3.5 inline mr-1" />
          Password (ถ้า PDF ติด password)
        </label>
        <div className="relative">
          <input
            type={showPwd ? "text" : "password"}
            value={passwords}
            onChange={(e) => setPasswords(e.target.value)}
            placeholder="ใส่ password หรือหลาย password คั่นด้วย , เช่น 0101,19901231"
            className={`w-full px-4 py-2.5 pr-10 rounded-xl border text-sm bg-white dark:bg-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              needPassword
                ? "border-red-400 ring-1 ring-red-400"
                : "border-gray-300 dark:border-gray-600"
            }`}
          />
          <button
            type="button"
            onClick={() => setShowPwd((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            {showPwd ? (
              <EyeOff className="w-4 h-4" />
            ) : (
              <Eye className="w-4 h-4" />
            )}
          </button>
        </div>
        {needPassword && (
          <p className="text-xs text-loss mt-1">
            PDF ต้องการ password — กรุณาใส่ด้านบน
          </p>
        )}
      </div>

      <button
        onClick={() => submit()}
        disabled={!file || loading}
        className={buttonClass('primary', 'md', 'w-full')}
      >
        {loading ? "⏳ กำลังประมวลผล..." : "📊 วิเคราะห์ใบแจ้งยอด"}
      </button>
    </div>
  );
};

// ── Result Dashboard ──────────────────────────────────────────────────────────
interface DashboardProps {
  result: ParseResult;
  history: StatementRecord[];
  onReset: () => void;
  onSaved?: () => void;
}

type CardProfiles = Record<string, { name?: string; image?: string | null }>;

function cardImage(
  cardType?: string,
  bank?: string,
  last4?: string,
  profiles?: CardProfiles,
): string | null {
  if (last4 && profiles?.[last4]?.image) return profiles[last4].image!;
  if (!cardType && bank === "TTB") return "/creditcards/ttb-absolute.webp";
  if (!cardType) return null;
  // UOB
  if (/WORLD/i.test(cardType)) return "/creditcards/uob-world-mc.webp";
  if (/PRIVI/i.test(cardType)) return "/creditcards/uob-privi-miles.webp";
  if (/PREMIER/i.test(cardType)) return "/creditcards/uob-premier.webp";
  if (/SIMPLE/i.test(cardType)) return "/creditcards/uob-simple-vs.webp";
  if (/\bONE\b/i.test(cardType)) return "/creditcards/uob-one-vs.webp";
  // AEON
  if (/NEXT.?GEN/i.test(cardType)) return "/creditcards/aeon-next-gen.webp";
  if (/M.?GEN/i.test(cardType)) return "/creditcards/aeon_mgen.webp";
  // KTC
  if (/UNIONPAY/i.test(cardType))
    return "/creditcards/ktc-unionpay-diamond.webp";
  if (/JCB/i.test(cardType)) return "/creditcards/ktc-jcb-ultimate.webp";
  if (/SIGNATURE/i.test(cardType))
    return "/creditcards/ktc-visa-signature.webp";
  if (/PLATINUM/i.test(cardType)) return "/creditcards/ktc-visa-platinum.webp";
  // TTB
  if (/SO.?FAST/i.test(cardType)) return "/creditcards/ttb-so-fast.webp";
  if (/SO.?SMART/i.test(cardType)) return "/creditcards/ttb-so-smart.webp";
  if (/ABSOLUTE/i.test(cardType)) return "/creditcards/ttb-absolute.webp";
  return null;
}

function cardDisplayName(
  cardType?: string,
  bank?: string,
  last4?: string,
  profiles?: CardProfiles,
): string {
  if (last4 && profiles?.[last4]?.name) return profiles[last4].name!;
  return cardType || bank || "";
}

const Dashboard: React.FC<DashboardProps> = ({
  result,
  history,
  onReset,
  onSaved,
}) => {
  const [filterCat, setFilterCat] = useState<string | null>(null);
  const [filterCard, setFilterCard] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const { getConfigValue, updateConfig } = useConfigStore();
  let cardProfiles: CardProfiles = {};
  try {
    cardProfiles = JSON.parse(getConfigValue("credit_card_profiles", "{}"));
  } catch {
    /**/
  }

  const saveStatement = async () => {
    // Duplicate detection: check if any card from this statement already has a record for the same month
    if (result.statement_month) {
      const incomingLast4s = Array.from(
        new Set(
          result.transactions
            .filter((t) => t.cardLast4)
            .map((t) => t.cardLast4!),
        ),
      );
      const duplicates = history.filter(
        (r) =>
          r.statement_month === result.statement_month &&
          r.bank === result.bank &&
          incomingLast4s.includes(r.card_last4),
      );
      if (duplicates.length > 0) {
        const labels = duplicates.map((r) => `*${r.card_last4}`).join(", ");
        const ok = window.confirm(
          `พบข้อมูลซ้ำ!\n\nบัตร ${labels} มีข้อมูลเดือน ${result.statement_month} อยู่แล้ว\n\nต้องการบันทึกทับข้อมูลเดิมหรือไม่?`,
        );
        if (!ok) return;
      }
    }

    setSaving(true);
    try {
      const res = await apiFetch("/api/credit-statements", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          bank: result.bank,
          period: result.period,
          transactions: result.transactions,
          points_by_card: result.points_by_card ?? {},
          points_expiry_by_card: result.points_expiry_by_card ?? {},
          earned_points_by_card: result.earned_points_by_card ?? {},
          card_sections: result.card_sections ?? {},
        }),
      });
      if (!res.ok) throw new Error();
      setSaved(true);
      toast.success("บันทึกแล้ว");
      onSaved?.();

      // Auto-save due dates detected from PDF
      if (result.cut_day != null || result.due_day != null) {
        const uniqueLast4s = Array.from(
          new Set(
            result.transactions
              .filter((t) => t.cardLast4)
              .map((t) => t.cardLast4!),
          ),
        );
        if (uniqueLast4s.length > 0) {
          let dueDates: DueDateMap = {};
          try {
            dueDates = JSON.parse(getConfigValue("credit_due_dates", "{}"));
          } catch {
            /**/
          }
          for (const last4 of uniqueLast4s) {
            const existing = dueDates[last4];
            const tx = result.transactions.find((t) => t.cardLast4 === last4);
            dueDates[last4] = {
              card_name:
                existing?.card_name ||
                cardDisplayName(tx?.cardType, result.bank, last4, cardProfiles),
              cut_day: result.cut_day ?? existing?.cut_day ?? 25,
              due_day: result.due_day ?? existing?.due_day ?? 15,
            };
          }
          try {
            await updateConfig(
              "credit_due_dates",
              JSON.stringify(dueDates),
              "Credit card due dates",
            );
            const parts = [
              result.cut_day != null ? `ตัดวันที่ ${result.cut_day}` : "",
              result.due_day != null ? `ชำระวันที่ ${result.due_day}` : "",
            ].filter(Boolean);
            toast(`📅 บันทึกวันชำระอัตโนมัติ: ${parts.join(" · ")}`, {
              duration: 4000,
            });
          } catch {
            /**/
          }
        }
      }
    } catch {
      toast.error("บันทึกไม่สำเร็จ");
    } finally {
      setSaving(false);
    }
  };

  // Unique cards (UOB may have multiple)
  const uniqueCards = Array.from(
    new Map(
      result.transactions
        .filter((t) => t.cardType && t.cardLast4)
        .map((t) => [
          t.cardLast4!,
          { cardType: t.cardType!, cardLast4: t.cardLast4! },
        ]),
    ).values(),
  );
  const hasMultipleCards = uniqueCards.length > 1;

  const CARD_PALETTES = [
    {
      active: "from-violet-500 to-purple-600",
      border: "hover:border-violet-400",
      dot: "bg-violet-500",
      text: "text-violet-600 dark:text-violet-400",
    },
    {
      active: "from-cyan-500 to-blue-600",
      border: "hover:border-cyan-400",
      dot: "bg-cyan-500",
      text: "text-cyan-600 dark:text-cyan-400",
    },
    {
      active: "from-rose-500 to-pink-600",
      border: "hover:border-rose-400",
      dot: "bg-rose-500",
      text: "text-primary-600 dark:text-primary-400",
    },
    {
      active: "from-amber-500 to-orange-600",
      border: "hover:border-amber-400",
      dot: "bg-amber-500",
      text: "text-amber-600 dark:text-amber-400",
    },
  ];
  const cardPaletteMap = Object.fromEntries(
    uniqueCards.map((c, i) => [
      c.cardLast4,
      CARD_PALETTES[i % CARD_PALETTES.length],
    ]),
  );

  const allCharges = result.transactions.filter((t) => !t.isCredit);
  const allCredits = result.transactions.filter((t) => t.isCredit);

  // Apply card filter
  const charges = filterCard
    ? allCharges.filter((t) => t.cardLast4 === filterCard)
    : allCharges;
  const credits = filterCard
    ? allCredits.filter((t) => t.cardLast4 === filterCard)
    : allCredits;
  const total = charges.reduce((s, t) => s + t.amount, 0);
  const totalCredit = credits.reduce((s, t) => s + t.amount, 0);

  // Recompute category summary from filtered charges
  const catSummary: Record<string, { amount: number; count: number }> = {};
  for (const t of charges) {
    if (!catSummary[t.category])
      catSummary[t.category] = { amount: 0, count: 0 };
    catSummary[t.category].amount += t.amount;
    catSummary[t.category].count += 1;
  }
  const sortedCats = Object.entries(catSummary).sort(
    (a, b) => b[1].amount - a[1].amount,
  );

  const pieData = sortedCats.map(([name, { amount }]) => ({
    name,
    value: amount,
  }));

  const filtered = filterCat
    ? charges.filter((t) => t.category === filterCat)
    : charges;

  const downloadCsv = () => {
    const bom = "﻿";
    const header = "วันที่,ร้านค้า,จำนวนเงิน,ประเภท,หมวดหมู่";
    const rows = charges.map(
      (t) =>
        `${t.date},"${t.merchant.replace(/"/g, '""')}",${t.amount},ค่าใช้จ่าย,${t.category}`,
    );
    const csv = bom + [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `credit_${result.bank}_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const bankLabel =
    result.bank === "unknown" ? "บัตรเครดิต" : `บัตรเครดิต ${result.bank}`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-primary-500" />
            {bankLabel}
          </h2>
          {result.period && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {result.period}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={downloadCsv}
            className={buttonClass('secondary', 'md')}
          >
            <Download className="w-4 h-4" />
            CSV
          </button>
          <button
            onClick={saveStatement}
            disabled={saving || saved}
            className={buttonClass(saved ? 'secondary' : 'primary', 'md')}
          >
            {saved ? "✓ บันทึกแล้ว" : saving ? "กำลังบันทึก..." : "บันทึก"}
          </button>
          <button
            onClick={onReset}
            className={buttonClass('primary', 'md')}
          >
            <Upload className="w-4 h-4" />
            อัปโหลดใหม่
          </button>
        </div>
      </div>

      {/* Detected due dates from PDF */}
      {!saved && (result.cut_day != null || result.due_day != null) && (
        <div className="flex items-center gap-2 text-xs text-primary-600 dark:text-primary-400 bg-brand-soft px-3 py-2 rounded-xl border border-primary-200 dark:border-primary-700">
          <CalendarDays className="w-3.5 h-3.5 shrink-0" />
          <span>พบจาก PDF:</span>
          {result.cut_day != null && (
            <span>
              ตัดบัญชีวันที่ <strong>{result.cut_day}</strong>
            </span>
          )}
          {result.cut_day != null && result.due_day != null && (
            <span className="text-primary-300 dark:text-primary-600">·</span>
          )}
          {result.due_day != null && (
            <span>
              ชำระวันที่ <strong>{result.due_day}</strong>
            </span>
          )}
          <span className="text-primary-400 dark:text-primary-500 ml-1">
            (จะบันทึกอัตโนมัติเมื่อกด บันทึก)
          </span>
        </div>
      )}

      {/* Card filter tabs (UOB multi-card) */}
      {hasMultipleCards && (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => {
              setFilterCard(null);
              setFilterCat(null);
            }}
            className={`inline-flex items-center justify-center px-4 h-9 rounded-full text-sm font-medium transition-colors ${
              !filterCard
                ? "bg-brand shadow-sm"
                : "bg-gradient-to-r from-blue-100 to-purple-100 hover:from-blue-200 hover:to-purple-200 text-primary-700 dark:from-blue-900/50 dark:to-purple-900/50 dark:hover:from-blue-900/70 dark:hover:to-purple-900/70 dark:text-blue-200"
            }`}
          >
            ทุกบัตร
          </button>
          {uniqueCards.map((c) => {
            const pal = cardPaletteMap[c.cardLast4];
            const isActive = filterCard === c.cardLast4;
            const img = cardImage(
              c.cardType,
              undefined,
              c.cardLast4,
              cardProfiles,
            );
            const name = cardDisplayName(
              c.cardType,
              undefined,
              c.cardLast4,
              cardProfiles,
            );
            return (
              <button
                key={c.cardLast4}
                onClick={() => {
                  setFilterCard(c.cardLast4);
                  setFilterCat(null);
                }}
                className={`flex items-center gap-2 px-4 h-9 rounded-full text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-brand shadow-sm"
                    : "bg-gradient-to-r from-blue-100 to-purple-100 hover:from-blue-200 hover:to-purple-200 text-primary-700 dark:from-blue-900/50 dark:to-purple-900/50 dark:hover:from-blue-900/70 dark:hover:to-purple-900/70 dark:text-blue-200"
                }`}
              >
                {img ? (
                  <img
                    src={img}
                    alt={name}
                    className="h-5 w-auto object-contain"
                  />
                ) : (
                  <span
                    className={`w-2 h-2 rounded-full ${isActive ? "bg-white/80" : pal.dot}`}
                  />
                )}
                {name} ···{c.cardLast4}
              </button>
            );
          })}
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-4 gap-2">
        {[
          { label: "รายการ", value: `${charges.length}`, color: "text-gray-900 dark:text-white" },
          { label: "ใช้จ่าย", value: `฿${fmt(total)}`, color: "text-gray-900 dark:text-white" },
          { label: "คืนเงิน", value: `฿${fmt(totalCredit)}`, color: "text-gain" },
          { label: "สุทธิ", value: `฿${fmt(total - totalCredit)}`, color: "text-gray-900 dark:text-white" },
        ].map((card) => (
          <div key={card.label} className="bg-gray-50 dark:bg-gray-800/50 rounded-xl px-3 py-2">
            <p className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5">{card.label}</p>
            <p className={`text-sm font-bold num ${card.color}`}>
              {card.value}
            </p>
          </div>
        ))}
      </div>

      {/* Charts + Category breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pie chart */}
        <div className="bg-transparent rounded-2xl p-5">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
            สัดส่วนค่าใช้จ่าย
          </h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={90}
                paddingAngle={2}
                dataKey="value"
              >
                {pieData.map((_, i) => (
                  <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number) => [`฿${fmt(value)}`, ""]}
                contentStyle={{
                  borderRadius: "12px",
                  border: "none",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Bar chart */}
        <div className="bg-transparent rounded-2xl p-5">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
            ยอดตามหมวดหมู่
          </h3>
          <ResponsiveContainer
            width="100%"
            height={sortedCats.length * 38 + 20}
          >
            <BarChart
              data={sortedCats.map(([name, { amount }]) => ({ name, amount }))}
              layout="vertical"
              margin={{ left: 4, right: 16 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                type="number"
                tickFormatter={(v) => `฿${(v / 1000).toFixed(0)}k`}
                tick={{ fontSize: 11 }}
              />
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fontSize: 12 }}
                width={110}
              />
              <Bar dataKey="amount" radius={[0, 6, 6, 0]}>
                {sortedCats.map((_, i) => (
                  <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                ))}
                <LabelList
                  dataKey="amount"
                  position="right"
                  formatter={(v: number) => `฿${fmt(v)}`}
                  style={{ fontSize: 11, fill: "#6b7280" }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Category pills */}
      <div className="bg-transparent rounded-2xl p-5">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          หมวดหมู่
        </h3>
        <div className="flex flex-wrap gap-2">
          {sortedCats.map(([cat, { amount, count }]) => {
            const cfg = CATEGORY_CONFIG[cat] ?? CATEGORY_CONFIG["อื่นๆ"];
            const active = filterCat === cat;
            return (
              <button
                key={cat}
                onClick={() => setFilterCat(active ? null : cat)}
                className={`flex items-center gap-1.5 px-3 h-9 rounded-xl text-sm font-medium transition-all ${
                  active
                    ? "bg-brand shadow-sm"
                    : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                }`}
              >
                <span className={active ? "text-white" : "text-gray-400 dark:text-gray-400"}>
                  {cfg.icon}
                </span>
                {cat}
                {active && (
                  <span className="text-xs text-white/80 font-normal">
                    ฿{fmt(amount)}
                  </span>
                )}
                {!active && (
                  <span className="text-xs bg-gray-200 dark:bg-gray-700 text-gray-400 px-1.5 py-0.5 rounded-md">
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Transaction table */}
      <div className="bg-transparent rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2 flex-wrap">
            รายการใช้จ่าย
            {filterCard && cardPaletteMap[filterCard] && (
              <span
                className={`inline-flex items-center gap-1 text-xs font-medium ${cardPaletteMap[filterCard].text}`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${cardPaletteMap[filterCard].dot}`}
                />
                {uniqueCards.find((c) => c.cardLast4 === filterCard)?.cardType}{" "}
                ···{filterCard}
              </span>
            )}
            {filterCat && (
              <span className="text-xs text-primary-500">· {filterCat}</span>
            )}
          </h3>
          {filterCat && (
            <button
              onClick={() => setFilterCat(null)}
              className="text-xs text-gray-400 hover:text-gray-600"
            >
              ล้างตัวกรอง ×
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left table-head">
                <th className="px-5 py-3">วันที่</th>
                <th className="px-5 py-3">ร้านค้า / รายการ</th>
                <th className="px-5 py-3">หมวดหมู่</th>
                {!filterCard && hasMultipleCards && (
                  <th className="px-5 py-3">บัตร</th>
                )}
                <th className="px-5 py-3 text-right">จำนวนเงิน</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              <AnimatePresence>
                {filtered.map((t, i) => {
                  const cfg =
                    CATEGORY_CONFIG[t.category] ?? CATEGORY_CONFIG["อื่นๆ"];
                  return (
                    <motion.tr
                      key={`${t.date}-${t.merchant}-${i}`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="border-l-2 border-l-transparent hover:border-l-primary-400 hover:bg-primary-50/30 dark:hover:bg-primary-900/10 transition-all duration-150"
                    >
                      <td className="px-5 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap font-mono text-xs">
                        {t.date}
                      </td>
                      <td className="px-5 py-3 text-gray-800 dark:text-gray-200 max-w-xs">
                        <span className="truncate block">{t.merchant}</span>
                      </td>
                      <td className="px-5 py-3">
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-white"
                          style={{ backgroundColor: cfg.color }}
                        >
                          {cfg.icon}
                          {t.category}
                        </span>
                      </td>
                      {!filterCard && hasMultipleCards && (
                        <td className="px-5 py-3 whitespace-nowrap">
                          {t.cardLast4 ? (
                            <span
                              className={`inline-flex items-center gap-1.5 text-xs font-medium ${cardPaletteMap[t.cardLast4]?.text ?? "text-gray-400"}`}
                            >
                              {cardImage(
                                t.cardType,
                                result.bank,
                                t.cardLast4,
                                cardProfiles,
                              ) ? (
                                <img
                                  src={
                                    cardImage(
                                      t.cardType,
                                      result.bank,
                                      t.cardLast4,
                                      cardProfiles,
                                    )!
                                  }
                                  alt={cardDisplayName(
                                    t.cardType,
                                    result.bank,
                                    t.cardLast4,
                                    cardProfiles,
                                  )}
                                  className="h-5 w-auto rounded object-contain"
                                />
                              ) : (
                                <span
                                  className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cardPaletteMap[t.cardLast4]?.dot ?? "bg-gray-400"}`}
                                />
                              )}
                              {cardDisplayName(
                                t.cardType,
                                result.bank,
                                t.cardLast4,
                                cardProfiles,
                              )}{" "}
                              ···{t.cardLast4}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                      )}
                      <td
                        className={`px-5 py-3 text-right font-semibold whitespace-nowrap ${
                          t.isCredit
                            ? "text-gain"
                            : "text-gray-900 dark:text-gray-100"
                        }`}
                      >
                        {t.isCredit ? "+" : ""}฿{fmt(t.amount)}
                      </td>
                    </motion.tr>
                  );
                })}
              </AnimatePresence>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ── helpers ───────────────────────────────────────────────────────────────────
const THAI_MONTH_LABELS = [
  "ม.ค.",
  "ก.พ.",
  "มี.ค.",
  "เม.ย.",
  "พ.ค.",
  "มิ.ย.",
  "ก.ค.",
  "ส.ค.",
  "ก.ย.",
  "ต.ค.",
  "พ.ย.",
  "ธ.ค.",
];
function fmtMonth(ym: string) {
  const [y, m] = ym.split("-");
  return `${THAI_MONTH_LABELS[parseInt(m) - 1]} ${y}`;
}
function prevMonthKey(ym: string) {
  const [y, m] = ym.split("-").map(Number);
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, "0")}`;
}

interface TxnRow {
  id: number;
  date: string;
  merchant: string;
  amount: number;
  is_credit: boolean;
  category: string;
  card_type: string;
  card_last4: string;
  statement_month?: string;
  bank?: string;
}

// ── History Dashboard ─────────────────────────────────────────────────────────
const HistoryDashboard: React.FC<{
  history: StatementRecord[];
  onRefresh: () => void;
  activeView: "monthly" | "overview" | "search";
}> = ({ history, onRefresh, activeView }) => {
  const availableYears = [
    ...new Set(history.map((r) => r.statement_month.slice(0, 4))),
  ]
    .sort()
    .reverse();
  const [selectedYear, setSelectedYear] = React.useState<string>(
    () => availableYears[0] ?? String(new Date().getFullYear()),
  );

  React.useEffect(() => {
    if (availableYears.length && !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears.join(",")]);

  const yearFilteredHistory = history.filter((r) =>
    r.statement_month.startsWith(selectedYear),
  );

  const months = [...new Set(yearFilteredHistory.map((r) => r.statement_month))]
    .sort()
    .reverse();
  const [sel, setSel] = useState(months[0] ?? "");
  const [txns, setTxns] = useState<TxnRow[]>([]);
  const [txnLoading, setTxnLoading] = useState(false);
  const [filterCat, setFilterCat] = useState<string | null>(null);
  const [filterCard, setFilterCard] = useState<string | null>(null);
  const [showAllCards, setShowAllCards] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [dropdownPos, setDropdownPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const searchDropRef = React.useRef<HTMLDivElement>(null);
  const [topMerchants, setTopMerchants] = useState<TopMerchant[]>([]);
  const [topMerchantsLoading, setTopMerchantsLoading] = useState(false);
  // Search state
  const [searchQ, setSearchQ] = useState("");
  const [searchCat, setSearchCat] = useState<string | null>(null);
  const [searchCard, setSearchCard] = useState<string | null>(null);
  const [searchFrom, setSearchFrom] = useState("");
  const [searchTo, setSearchTo] = useState("");
  const [searchResults, setSearchResults] = useState<TxnRow[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTotal, setSearchTotal] = useState(0);
  const [searchEditId, setSearchEditId] = useState<number | null>(null);
  const [searchDropPos, setSearchDropPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const searchDebounce = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const monthScrollRef = React.useRef<HTMLDivElement>(null);
  const { getConfigValue } = useConfigStore();
  let cardProfiles: CardProfiles = {};
  try {
    cardProfiles = JSON.parse(getConfigValue("credit_card_profiles", "{}"));
  } catch {
    /**/
  }

  // Navigate away if selected month was deleted
  React.useEffect(() => {
    if (sel && !months.includes(sel)) setSel(months[0] ?? "");
  }, [months.join(",")]);

  React.useEffect(() => {
    if (months.length && !sel) setSel(months[0]);
  }, [months.length]);

  // Auto-scroll month bar to the right (newest month) on load
  React.useEffect(() => {
    if (monthScrollRef.current) {
      monthScrollRef.current.scrollLeft = monthScrollRef.current.scrollWidth;
    }
  }, [months.length]);

  React.useEffect(() => {
    if (!editingId) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current?.contains(e.target as Node)) return;
      setEditingId(null);
      setDropdownPos(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [editingId]);

  React.useEffect(() => {
    if (!sel) return;
    setTxnLoading(true);
    setFilterCat(null);
    setFilterCard(null);
    setEditingId(null);
    apiFetch(`/api/credit-transactions?month=${sel}`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setTxns)
      .finally(() => setTxnLoading(false));
  }, [sel]);

  React.useEffect(() => {
    if (activeView !== "overview") return;
    setTopMerchantsLoading(true);
    apiFetch(`/api/credit-merchants/top?limit=10&year=${selectedYear}`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setTopMerchants)
      .finally(() => setTopMerchantsLoading(false));
  }, [activeView, history.length, selectedYear]);

  // Close search category dropdown on outside click
  React.useEffect(() => {
    if (!searchEditId) return;
    const handler = (e: MouseEvent) => {
      if (searchDropRef.current?.contains(e.target as Node)) return;
      setSearchEditId(null);
      setSearchDropPos(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [searchEditId]);

  const runSearch = React.useCallback(
    (
      q: string,
      cat: string | null,
      card: string | null,
      from: string,
      to: string,
    ) => {
      if (searchDebounce.current) clearTimeout(searchDebounce.current);
      searchDebounce.current = setTimeout(async () => {
        if (!q && !cat && !card && !from && !to) {
          setSearchResults([]);
          setSearchTotal(0);
          return;
        }
        setSearchLoading(true);
        const params = new URLSearchParams();
        if (q) params.set("q", q);
        if (cat) params.set("category", cat);
        if (card) params.set("card_last4", card);
        if (from) params.set("from_month", from);
        if (to) params.set("to_month", to);
        params.set("limit", "100");
        try {
          const res = await apiFetch(
            `/api/credit-transactions/search?${params}`,
          );
          const data = res.ok ? await res.json() : { results: [], total: 0 };
          setSearchResults(data.results ?? []);
          setSearchTotal(data.total ?? 0);
        } finally {
          setSearchLoading(false);
        }
      }, 300);
    },
    [],
  );

  const updateSearchCategory = async (txnId: number, newCat: string) => {
    const res = await apiFetch(`/api/credit-transactions/${txnId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ category: newCat }),
    });
    if (!res.ok) {
      toast.error("อัปเดตหมวดหมู่ไม่สำเร็จ");
      return;
    }
    setSearchResults((prev) =>
      prev.map((t) => (t.id === txnId ? { ...t, category: newCat } : t)),
    );
    setSearchEditId(null);
    setSearchDropPos(null);
    onRefresh();
  };

  const updateCategory = async (txnId: number, newCat: string) => {
    const res = await apiFetch(`/api/credit-transactions/${txnId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ category: newCat }),
    });
    if (!res.ok) {
      toast.error("อัปเดตหมวดหมู่ไม่สำเร็จ");
      return;
    }
    setTxns((prev) =>
      prev.map((t) => (t.id === txnId ? { ...t, category: newCat } : t)),
    );
    setEditingId(null);
    setDropdownPos(null);
    onRefresh();
  };

  const deleteStatement = async (stmtId: number, label: string) => {
    if (
      !window.confirm(
        `ลบ statement "${label}" ?\n\nรายการทั้งหมดของบัตรนี้ในเดือนนี้จะถูกลบถาวร`,
      )
    )
      return;
    const res = await apiFetch(`/api/credit-statements/${stmtId}`, {
      method: "DELETE",
    });
    if (!res.ok) {
      toast.error("ลบไม่สำเร็จ");
      return;
    }
    toast.success("ลบ statement แล้ว");
    setFilterCard(null);
    onRefresh();
  };

  const viewStatementPdf = async (stmtId: number) => {
    try {
      const res = await apiFetch(`/api/credit-statements/${stmtId}/pdf`);
      if (!res.ok) {
        toast.error("ไม่พบไฟล์ PDF ต้นฉบับ");
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      toast.error("เปิด PDF ไม่สำเร็จ");
    }
  };

  const openDropdown = (
    e: React.MouseEvent<HTMLButtonElement>,
    txnId: number,
  ) => {
    e.stopPropagation();
    if (editingId === txnId) {
      setEditingId(null);
      setDropdownPos(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const dropdownHeight = Object.keys(CATEGORY_CONFIG).length * 36 + 8;
    const spaceBelow = window.innerHeight - rect.bottom;
    const top =
      spaceBelow < dropdownHeight
        ? rect.top - dropdownHeight - 4
        : rect.bottom + 4;
    setDropdownPos({ top, left: rect.left });
    setEditingId(txnId);
  };

  const recs = yearFilteredHistory.filter((r) => r.statement_month === sel);
  const prev = history.filter((r) => r.statement_month === prevMonthKey(sel));

  const filteredRecs = filterCard
    ? recs.filter((r) => r.card_last4 === filterCard)
    : recs;
  const monthTotal = filteredRecs.reduce((s, r) => s + r.total_spent, 0);
  const monthCredit = filteredRecs.reduce((s, r) => s + r.total_credit, 0);

  const monthPoints = filteredRecs.reduce(
    (s, r) => s + (r.total_points || 0),
    0,
  );
  const monthEarned = filteredRecs.reduce(
    (s, r) => s + (r.earned_points || 0),
    0,
  );
  const prevTotal = prev.reduce((s, r) => s + r.total_spent, 0);
  const diff = prevTotal > 0 ? monthTotal - prevTotal : null;
  const prevPoints = prev.reduce((s, r) => s + (r.total_points || 0), 0);
  const pointsDiff = prevPoints > 0 ? monthPoints - prevPoints : null;
  // earned this month: prefer stored earned_points, fall back to diff vs prev month
  const earnedThisMonth =
    monthEarned > 0
      ? monthEarned
      : pointsDiff != null && pointsDiff > 0
        ? pointsDiff
        : null;

  // Merge category summaries for selected/filtered cards
  const catMap: CategorySummary = {};
  for (const r of filteredRecs) {
    for (const [cat, v] of Object.entries(r.category_summary)) {
      if (!catMap[cat]) catMap[cat] = { amount: 0, count: 0 };
      catMap[cat].amount = +(catMap[cat].amount + v.amount).toFixed(2);
      catMap[cat].count += v.count;
    }
  }
  const sortedCats = Object.entries(catMap).sort(
    (a, b) => b[1].amount - a[1].amount,
  );

  const cardChartData = Object.values(
    recs.reduce(
      (acc, r) => {
        const key = r.card_last4 || "unknown";
        if (!acc[key])
          acc[key] = {
            name: cardDisplayName(
              r.card_type || undefined,
              r.bank,
              r.card_last4 || undefined,
              cardProfiles,
            ),
            amount: 0,
          };
        acc[key].amount += r.total_spent;
        return acc;
      },
      {} as Record<string, { name: string; amount: number }>,
    ),
  ).filter((d) => d.amount > 0).sort((a, b) => b.amount - a.amount);

  const catChartHeight = sortedCats.length * 28 + 15;
  const cardChartHeight = cardChartData.length * 32 + 15;
  const sharedChartHeight = Math.max(catChartHeight, cardChartHeight);

  // Trend: total per month (asc)
  const trendData = [...months].reverse().map((ym) => ({
    label: fmtMonth(ym),
    total: yearFilteredHistory
      .filter((r) => r.statement_month === ym)
      .reduce((s, r) => s + r.total_spent, 0),
    isSel: ym === sel,
  }));

  // Overview: unique cards across all history
  const allCards = Array.from(
    new Map(history.map((r) => [`${r.bank}|${r.card_last4}`, r])).values(),
  ).map((r, i) => ({
    key: `pts_${r.card_last4 || i}`,
    last4: r.card_last4,
    bank: r.bank,
    name: cardDisplayName(
      r.card_type || undefined,
      r.bank,
      r.card_last4 || undefined,
      cardProfiles,
    ),
    color: PIE_COLORS[i % PIE_COLORS.length],
  }));

  const pointsTrendData = [...months].reverse().map((ym) => {
    const entry: Record<string, string | number> = { label: fmtMonth(ym) };
    for (const c of allCards) {
      const rec = history.find(
        (r) => r.statement_month === ym && r.card_last4 === c.last4,
      );
      entry[c.key] = rec?.total_points ?? 0;
    }
    return entry;
  });

  // Expiry alerts for filtered cards in selected month
  const expiryAlerts = filteredRecs.filter((r) => (r.expiring_points ?? 0) > 0);

  if (months.length === 0 && availableYears.length === 0) return null;

  return (
    <div className="space-y-4">
      {months.length === 0 && activeView !== "search" && (
        <div className="flex items-center gap-3 py-4">
          {availableYears.length > 1 && (
            <div className="flex gap-1.5 flex-wrap">
              {availableYears.map((year) => (
                <button
                  key={year}
                  onClick={() => setSelectedYear(year)}
                  className={`inline-flex items-center justify-center px-3 h-9 rounded-full text-xs font-semibold transition-colors ${
                    selectedYear === year
                      ? "bg-brand shadow-sm"
                      : "text-gray-400 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                  }`}
                >
                  {year}
                </button>
              ))}
            </div>
          )}
          <p className="text-sm text-gray-400 dark:text-gray-400">
            ไม่มีข้อมูลปี {selectedYear}
          </p>
        </div>
      )}

      {activeView === "search" ? (
        /* ── Search tab ── */
        <div className="space-y-4">
          {/* Search bar */}
          <div className="bg-transparent rounded-2xl p-4 space-y-3">
            <div className="relative">
              <input
                type="text"
                placeholder="ค้นหาร้านค้า / รายการ..."
                value={searchQ}
                onChange={(e) => {
                  setSearchQ(e.target.value);
                  runSearch(
                    e.target.value,
                    searchCat,
                    searchCard,
                    searchFrom,
                    searchTo,
                  );
                }}
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-sm text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                autoFocus
              />
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>

            {/* Filters row */}
            <div className="flex flex-wrap gap-2 items-center">
              {/* Category filter */}
              <select
                value={searchCat ?? ""}
                onChange={(e) => {
                  const v = e.target.value || null;
                  setSearchCat(v);
                  runSearch(searchQ, v, searchCard, searchFrom, searchTo);
                }}
                className="text-xs px-3 py-1.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">ทุกหมวดหมู่</option>
                {Object.keys(CATEGORY_CONFIG).map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>

              {/* Card filter */}
              {allCards.length > 1 && (
                <select
                  value={searchCard ?? ""}
                  onChange={(e) => {
                    const v = e.target.value || null;
                    setSearchCard(v);
                    runSearch(searchQ, searchCat, v, searchFrom, searchTo);
                  }}
                  className="text-xs px-3 py-1.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">ทุกบัตร</option>
                  {allCards.map((c) => (
                    <option key={c.key} value={c.last4}>
                      {c.name} ···{c.last4}
                    </option>
                  ))}
                </select>
              )}

              {/* Date range */}
              <input
                type="month"
                value={searchFrom}
                max={searchTo || undefined}
                onChange={(e) => {
                  setSearchFrom(e.target.value);
                  runSearch(
                    searchQ,
                    searchCat,
                    searchCard,
                    e.target.value,
                    searchTo,
                  );
                }}
                className="text-xs px-3 py-1.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <span className="text-xs text-gray-400">—</span>
              <input
                type="month"
                value={searchTo}
                min={searchFrom || undefined}
                onChange={(e) => {
                  setSearchTo(e.target.value);
                  runSearch(
                    searchQ,
                    searchCat,
                    searchCard,
                    searchFrom,
                    e.target.value,
                  );
                }}
                className="text-xs px-3 py-1.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />

              {/* Clear */}
              {(searchQ ||
                searchCat ||
                searchCard ||
                searchFrom ||
                searchTo) && (
                <button
                  onClick={() => {
                    setSearchQ("");
                    setSearchCat(null);
                    setSearchCard(null);
                    setSearchFrom("");
                    setSearchTo("");
                    setSearchResults([]);
                    setSearchTotal(0);
                  }}
                  className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                >
                  ล้าง ×
                </button>
              )}
            </div>
          </div>

          {/* Results */}
          <div className="bg-transparent rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                {searchLoading
                  ? "กำลังค้นหา..."
                  : searchTotal > 0
                    ? `พบ ${searchTotal} รายการ`
                    : searchQ ||
                        searchCat ||
                        searchCard ||
                        searchFrom ||
                        searchTo
                      ? "ไม่พบรายการ"
                      : "กรอกคำค้นหาด้านบน"}
              </h3>
              {searchTotal > 0 && (
                <span className="text-xs text-gray-400">
                  ฿{fmt(searchResults.reduce((s, t) => s + t.amount, 0))} รวม
                </span>
              )}
            </div>

            {searchLoading ? (
              <div className="px-5 py-10 text-center text-sm text-gray-400">
                กำลังค้นหา...
              </div>
            ) : searchResults.length === 0 ? (
              <div className="px-5 py-10 text-center text-sm text-gray-400">
                {!searchQ &&
                !searchCat &&
                !searchCard &&
                !searchFrom &&
                !searchTo
                  ? "ใส่ชื่อร้านค้า หมวดหมู่ หรือช่วงเวลาเพื่อค้นหา"
                  : "ไม่พบรายการที่ตรงกัน"}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left table-head">
                      <th className="px-5 py-3">เดือน</th>
                      <th className="px-5 py-3">วันที่</th>
                      <th className="px-5 py-3">ร้านค้า / รายการ</th>
                      <th className="px-5 py-3">หมวดหมู่</th>
                      {allCards.length > 1 && (
                        <th className="px-5 py-3">บัตร</th>
                      )}
                      <th className="px-5 py-3 text-right">จำนวนเงิน</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {searchResults.map((t) => {
                      const cfg =
                        CATEGORY_CONFIG[t.category] ?? CATEGORY_CONFIG["อื่นๆ"];
                      const cardImg = cardImage(
                        t.card_type || undefined,
                        t.bank,
                        t.card_last4 || undefined,
                        cardProfiles,
                      );
                      const cardName = cardDisplayName(
                        t.card_type || undefined,
                        t.bank,
                        t.card_last4 || undefined,
                        cardProfiles,
                      );
                      return (
                        <tr
                          key={t.id}
                          className="border-l-2 border-l-transparent hover:border-l-primary-400 hover:bg-primary-50/30 dark:hover:bg-primary-900/10 transition-all duration-150"
                        >
                          <td className="px-5 py-3 text-xs text-gray-400 dark:text-gray-400 whitespace-nowrap font-mono">
                            {t.statement_month
                              ? fmtMonth(t.statement_month)
                              : "—"}
                          </td>
                          <td className="px-5 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap font-mono text-xs">
                            {t.date}
                          </td>
                          <td className="px-5 py-3 text-gray-800 dark:text-gray-200 max-w-xs">
                            <span className="truncate block">{t.merchant}</span>
                          </td>
                          <td className="px-5 py-3">
                            <button
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                if (searchEditId === t.id) {
                                  setSearchEditId(null);
                                  setSearchDropPos(null);
                                  return;
                                }
                                const rect =
                                  e.currentTarget.getBoundingClientRect();
                                const dropH =
                                  Object.keys(CATEGORY_CONFIG).length * 36 + 8;
                                const top =
                                  window.innerHeight - rect.bottom < dropH
                                    ? rect.top - dropH - 4
                                    : rect.bottom + 4;
                                setSearchDropPos({ top, left: rect.left });
                                setSearchEditId(t.id);
                              }}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-white cursor-pointer"
                              style={{ backgroundColor: cfg.color }}
                            >
                              <span className="flex-shrink-0 flex items-center">
                                {cfg.icon}
                              </span>
                              {t.category}
                            </button>
                          </td>
                          {allCards.length > 1 && (
                            <td className="px-5 py-3 whitespace-nowrap">
                              {t.card_last4 ? (
                                <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                                  {cardImg && (
                                    <img
                                      src={cardImg}
                                      className="h-4 w-auto rounded object-contain"
                                      alt=""
                                    />
                                  )}
                                  {cardName} ···{t.card_last4}
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                          )}
                          <td className="px-5 py-3 text-right font-semibold whitespace-nowrap text-gray-900 dark:text-gray-100">
                            ฿{fmt(t.amount)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Search category dropdown portal */}
          {searchEditId !== null &&
            searchDropPos &&
            createPortal(
              <div
                ref={searchDropRef}
                className="fixed z-[9999] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl py-1 min-w-[180px]"
                style={{ top: searchDropPos.top, left: searchDropPos.left }}
              >
                {Object.entries(CATEGORY_CONFIG).map(([cat, catCfg]) => {
                  const current = searchResults.find(
                    (t) => t.id === searchEditId,
                  )?.category;
                  return (
                    <button
                      key={cat}
                      onClick={() => updateSearchCategory(searchEditId, cat)}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${cat === current ? "font-semibold" : ""}`}
                    >
                      <span
                        className="w-5 h-5 rounded-full flex items-center justify-center text-white flex-shrink-0"
                        style={{ backgroundColor: catCfg.color }}
                      >
                        {catCfg.icon}
                      </span>
                      <span className="text-gray-700 dark:text-gray-300">
                        {cat}
                      </span>
                      {cat === current && (
                        <span className="ml-auto text-gray-400">✓</span>
                      )}
                    </button>
                  );
                })}
              </div>,
              document.body,
            )}
        </div>
      ) : activeView === "overview" ? (
        /* ── Overview tab ── */
        <div className="space-y-5">
          {/* Year selector */}
          {availableYears.length > 1 && (
            <div className="flex items-center gap-1.5">
              {availableYears.map((year) => (
                <button
                  key={year}
                  onClick={() => setSelectedYear(year)}
                  className={`inline-flex items-center justify-center px-3 h-9 rounded-full text-xs font-semibold transition-colors ${
                    selectedYear === year
                      ? "bg-brand shadow-sm"
                      : "text-gray-400 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                  }`}
                >
                  {year}
                </button>
              ))}
            </div>
          )}

          {/* Points trend chart */}
          {pointsTrendData.length > 0 &&
            allCards.some((c) =>
              pointsTrendData.some((d) => (d[c.key] as number) > 0),
            ) && (
              <div className="bg-transparent rounded-2xl p-5">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  คะแนนสะสมย้อนหลัง
                </h3>
                <p className="text-xs text-gray-400 dark:text-gray-400 mb-4">
                  คะแนนคงเหลือของแต่ละบัตรรายเดือน
                </p>
                <div className="flex flex-wrap gap-3 mb-3">
                  {allCards.map((c) => (
                    <span
                      key={c.key}
                      className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300"
                    >
                      <span
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: c.color }}
                      />
                      {c.name}
                      {c.last4 && (
                        <span className="font-mono opacity-60">
                          ···{c.last4}
                        </span>
                      )}
                    </span>
                  ))}
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart
                    data={pointsTrendData}
                    margin={{ left: 4, right: 4 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis
                      tickFormatter={(v) =>
                        v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)
                      }
                      tick={{ fontSize: 11 }}
                      width={45}
                    />
                    <Tooltip
                      formatter={(v: number, name: string) => {
                        const card = allCards.find((c) => c.key === name);
                        return [
                          v.toLocaleString(),
                          card ? `${card.name} ···${card.last4}` : name,
                        ];
                      }}
                      contentStyle={{
                        borderRadius: "12px",
                        border: "none",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                      }}
                    />
                    {allCards.map((c) => (
                      <Bar
                        key={c.key}
                        dataKey={c.key}
                        fill={c.color}
                        radius={[4, 4, 0, 0]}
                        maxBarSize={40}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

          {/* Top merchants */}
          <div className="bg-transparent rounded-2xl p-5">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
              Top 10 ร้านค้า
            </h3>
            <p className="text-xs text-gray-400 dark:text-gray-400 mb-4">
              ยอดใช้จ่ายรวมย้อนหลัง 12 เดือน
            </p>
            {topMerchantsLoading ? (
              <p className="text-sm text-gray-400 py-6 text-center">
                กำลังโหลด...
              </p>
            ) : topMerchants.length === 0 ? (
              <p className="text-sm text-gray-400 py-6 text-center">
                ไม่มีข้อมูล
              </p>
            ) : (
              <>
                <ResponsiveContainer
                  width="100%"
                  height={topMerchants.length * 38 + 20}
                >
                  <BarChart
                    data={topMerchants}
                    layout="vertical"
                    margin={{ left: 8, right: 60 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      type="number"
                      tickFormatter={(v) => `฿${(v / 1000).toFixed(0)}k`}
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis
                      type="category"
                      dataKey="merchant"
                      tick={{ fontSize: 11 }}
                      width={140}
                    />
                    <Tooltip
                      formatter={(v: number) => [`฿${fmt(v)}`, "ยอด"]}
                      contentStyle={{
                        borderRadius: "12px",
                        border: "none",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                      }}
                    />
                    <Bar dataKey="total" radius={[0, 6, 6, 0]}>
                      {topMerchants.map((_, i) => (
                        <Cell
                          key={i}
                          fill={PIE_COLORS[i % PIE_COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-3 space-y-1">
                  {topMerchants.map((m, i) => {
                    const cfg =
                      CATEGORY_CONFIG[m.category] ?? CATEGORY_CONFIG["อื่นๆ"];
                    return (
                      <div
                        key={m.merchant}
                        className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400"
                      >
                        <span className="w-5 text-right font-mono opacity-50">
                          {i + 1}.
                        </span>
                        <span
                          className="w-4 h-4 rounded-full flex items-center justify-center text-white flex-shrink-0"
                          style={{ backgroundColor: cfg.color, fontSize: 9 }}
                        >
                          {cfg.icon}
                        </span>
                        <span className="flex-1 truncate text-gray-700 dark:text-gray-300">
                          {m.merchant}
                        </span>
                        <span className="font-semibold text-gray-800 dark:text-gray-200">
                          ฿{fmt(m.total)}
                        </span>
                        <span className="opacity-50">{m.count} ครั้ง</span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        /* ── Monthly tab ── */
        <>
          {/* Year pills + Month scroll (with per-month spend bar + amount) — single combined row */}
          {months.length > 0 && (() => {
            const ordered = [...months].reverse();
            const monthNames = ["","ม.ค.","ก.พ.","มี.ค.","เม.ย.","พ.ค.","มิ.ย.","ก.ค.","ส.ค.","ก.ย.","ต.ค.","พ.ย.","ธ.ค."];
            const monthNamesEn = ["","Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
            const spendByMonth: Record<string, number> = {};
            for (const r of yearFilteredHistory) spendByMonth[r.statement_month] = (spendByMonth[r.statement_month] ?? 0) + r.total_spent;
            const maxSpend = Math.max(...ordered.map((ym) => spendByMonth[ym] ?? 0), 1);
            // Spend intensity ramp: light violet (lowest month) → deep pink (highest month) within the shown months
            const spends = ordered.map((ym) => spendByMonth[ym] ?? 0);
            const minSpend = Math.min(...spends);
            const spendColor = (spend: number) => {
              const k = maxSpend > minSpend ? (spend - minSpend) / (maxSpend - minSpend) : 0.5;
              return `hsl(${Math.round(255 + 75 * k)} ${Math.round(70 + 15 * k)}% ${Math.round(84 - 30 * k)}%)`;
            };
            // Newest first — selected year sits on top, older years fade out downward
            const yearIdx = Math.max(availableYears.indexOf(selectedYear), 0);
            const YEAR_SLOT = 28;
            return (
              <div className="flex items-center gap-2">
                {/* Year picker (vertical) */}
                <div className="flex flex-col items-center shrink-0 w-14">
                  <button
                    onClick={() => setSelectedYear(availableYears[yearIdx - 1])}
                    disabled={yearIdx <= 0}
                    aria-label="ปีถัดไป"
                    className="p-0.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 dark:hover:text-gray-200 disabled:opacity-30 disabled:pointer-events-none transition-colors"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <div
                    className="relative overflow-hidden w-full"
                    style={{
                      height: YEAR_SLOT * 2,
                      maskImage: "linear-gradient(to bottom, #000 55%, transparent)",
                      WebkitMaskImage: "linear-gradient(to bottom, #000 55%, transparent)",
                    }}
                  >
                    <div
                      className="flex flex-col transition-transform duration-300 ease-out"
                      style={{ transform: `translateY(${-yearIdx * YEAR_SLOT}px)` }}
                    >
                      {availableYears.map((year, i) => {
                        const isSel = i === yearIdx;
                        return (
                          <button
                            key={year}
                            onClick={() => setSelectedYear(year)}
                            style={{ height: YEAR_SLOT, opacity: isSel ? 1 : 0.6 }}
                            className={`shrink-0 w-full text-center tabular-nums transition-all duration-300 ${
                              isSel
                                ? "text-sm font-semibold bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent"
                                : "text-xs font-medium text-gray-500 dark:text-gray-400"
                            }`}
                          >
                            {year}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedYear(availableYears[yearIdx + 1])}
                    disabled={yearIdx >= availableYears.length - 1}
                    aria-label="ปีก่อนหน้า"
                    className="p-0.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 dark:hover:text-gray-200 disabled:opacity-30 disabled:pointer-events-none transition-colors"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>
                <div className="w-px self-stretch bg-gray-200 dark:bg-gray-700 shrink-0 my-2" />

                {/* Month scroll — bar + label + amount combined */}
                <div ref={monthScrollRef} className="flex-1 flex items-center gap-1 overflow-x-auto min-w-0" style={{ scrollbarWidth: "none" }}>
                  {ordered.map((ym) => {
                    const isActive = sel === ym;
                    const spend = spendByMonth[ym] ?? 0;
                    const pct = Math.round((spend / maxSpend) * 100);
                    const m = ym.split("-")[1];
                    const label = monthNames[parseInt(m)] ?? m;
                    const labelEn = monthNamesEn[parseInt(m)] ?? "";
                    return (
                      <button
                        key={ym}
                        onClick={() => setSel(ym)}
                        className={`flex-shrink-0 flex flex-col items-center px-3 pt-2 pb-2 rounded-xl min-w-[68px] transition-all ${
                          isActive
                            ? "bg-brand-soft ring-1 ring-primary-200 dark:ring-primary-800"
                            : "hover:bg-gray-100 dark:hover:bg-gray-800"
                        }`}
                      >
                        <div className="flex items-end h-8 w-full justify-center mb-1.5">
                          <div
                            className="w-5 rounded-t-[5px] rounded-b-sm transition-all duration-300"
                            style={{ height: `${Math.max(pct, 12)}%`, backgroundColor: spendColor(spend) }}
                          />
                        </div>
                        <span className={`text-sm font-semibold leading-tight whitespace-nowrap ${isActive ? "text-primary-600 dark:text-primary-300" : "text-gray-700 dark:text-gray-200"}`}>
                          {label}
                        </span>
                        <span className={`text-[10px] leading-tight whitespace-nowrap ${isActive ? "text-primary-500 dark:text-primary-300" : "text-gray-400 dark:text-gray-500"}`}>
                          {labelEn}
                        </span>
                        <span className={`mt-1 text-xs font-semibold tabular-nums leading-none ${isActive ? "text-primary-600 dark:text-primary-300" : "text-gray-500 dark:text-gray-400"}`}>
                          {spend >= 1000 ? `฿${(spend / 1000).toFixed(1).replace(/\.0$/, "")}k` : spend > 0 ? `฿${Math.round(spend)}` : "-"}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Card selector */}
          {recs.length > 0 && (
            <div className="flex flex-wrap gap-1.5 border-t border-gray-100 dark:border-gray-700/60 pt-4">
              <button
                onClick={() => setFilterCard(null)}
                className={`flex items-center gap-1 px-2.5 rounded-xl text-xs font-semibold transition-colors ${
                  !filterCard
                    ? "bg-brand-soft text-primary-600 dark:text-primary-400"
                    : "text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 dark:text-gray-400"
                }`}
              >
                <CreditCard className="w-3.5 h-3.5" />
                ทุกบัตร
                <span className="opacity-60 font-normal">({recs.length})</span>
              </button>
              <div className="w-px self-stretch bg-gray-200 dark:bg-gray-700 my-1.5 mx-1" />

              {(() => {
                const sortedRecs = [...recs].sort((a, b) => b.total_spent - a.total_spent);
                const zeroCount = sortedRecs.filter((r) => r.total_spent <= 0 && r.card_last4 !== filterCard).length;
                const visibleRecs = showAllCards
                  ? sortedRecs
                  : sortedRecs.filter((r) => r.total_spent > 0 || r.card_last4 === filterCard);
                return (
                  <>
                    {visibleRecs.map((r) => {
                  const img = cardImage(r.card_type || undefined, r.bank, r.card_last4 || undefined, cardProfiles);
                  const name = cardDisplayName(r.card_type || undefined, r.bank, r.card_last4 || undefined, cardProfiles);
                  const isActive = filterCard === r.card_last4;
                  const hasSpend = r.total_spent > 0;
                  const label = `${name} ···${r.card_last4 || "----"} (${fmtMonth(sel)})`;
                  return (
                    <div key={r.id} className="relative group w-[152px]">
                      <button
                        onClick={() => setFilterCard(isActive ? null : r.card_last4 || null)}
                        className={`w-full flex items-center gap-2 p-1.5 rounded-xl border text-left transition-all ${
                          isActive
                            ? "border-primary-300 dark:border-primary-500/50 bg-brand-soft ring-1 ring-primary-200 dark:ring-primary-800/60"
                            : "border-gray-100 dark:border-gray-700/70 bg-white dark:bg-gray-800/40 hover:border-gray-200 dark:hover:border-gray-600"
                        } ${!hasSpend ? "opacity-55" : ""}`}
                      >
                        {img ? (
                          <img src={img} alt={name} className="h-6 w-9 rounded object-cover shadow-sm flex-shrink-0" />
                        ) : (
                          <div className="h-6 w-9 rounded bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                            <CreditCard className="w-3.5 h-3.5 text-gray-400" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline gap-1 text-[10px] leading-tight">
                            <span className="font-medium text-gray-500 dark:text-gray-400 truncate min-w-0">{name}</span>
                            <span className="font-mono text-gray-400 dark:text-gray-400 flex-shrink-0">···{r.card_last4 || "----"}</span>
                          </div>
                          <div
                            className={`text-[13px] font-bold tabular-nums leading-tight ${
                              isActive
                                ? "text-primary-600 dark:text-primary-400"
                                : hasSpend
                                  ? "text-gray-800 dark:text-gray-100"
                                  : "text-gray-400 dark:text-gray-400"
                            }`}
                          >
                            ฿{Math.round(r.total_spent).toLocaleString("th-TH")}
                          </div>
                        </div>
                      </button>

                      <div className="absolute top-1 right-1 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm rounded-md p-0.5 shadow-sm">
                        {r.pdf_path && (
                          <button
                            onClick={(e) => { e.stopPropagation(); viewStatementPdf(r.id); }}
                            title="เปิด PDF ต้นฉบับ"
                            className="p-0.5 rounded text-blue-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                          >
                            <FileText className="w-2.5 h-2.5" />
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteStatement(r.id, label); }}
                          title="ลบ statement"
                          className="p-0.5 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </div>
                    </div>
                  );
                    })}
                    {zeroCount > 0 && (
                      <button
                        onClick={() => setShowAllCards((v) => !v)}
                        className="flex-shrink-0 flex items-center gap-1 px-2.5 h-full rounded-xl text-xs font-medium text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 dark:text-gray-400 transition-colors"
                      >
                        {showAllCards ? (
                          <>ซ่อนบัตรที่ไม่มีรายการ</>
                        ) : (
                          <>+{zeroCount} บัตรไม่มีรายการ</>
                        )}
                      </button>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {/* Summary stats */}
          <div className="grid grid-cols-4 gap-2">
            {[
              { label: "ใช้จ่าย", value: `฿${fmt(monthTotal)}`, sub: diff !== null && !filterCard ? { text: `${diff > 0 ? "▲" : "▼"} ฿${fmt(Math.abs(diff))}`, positive: diff <= 0 } : null, bg: "stat-tile-violet", label_color: "stat-title", value_color: "stat-value" },
              { label: "เงินคืน", value: `฿${fmt(monthCredit)}`, sub: null, bg: "stat-tile-gain", label_color: "stat-title", value_color: "stat-value" },
              { label: "สุทธิ",   value: `฿${fmt(monthTotal - monthCredit)}`, sub: null, bg: "stat-tile-loss", label_color: "stat-title", value_color: "stat-value" },
              { label: "คะแนน",  value: earnedThisMonth !== null ? `+${earnedThisMonth.toLocaleString()}` : "—", sub: null, bg: "stat-tile-primary", label_color: "stat-title", value_color: "stat-value" },
            ].map(({ label, value, sub, bg, label_color, value_color }) => (
              <div key={label} className={`${bg} rounded-2xl px-4 py-3`}>
                <p className={`mb-1 ${label_color}`}>{label}</p>
                <p className={`${value_color} !text-base`}>{value}</p>
                {sub && (
                  <p className={`text-[10px] mt-0.5 font-medium ${sub.positive ? "text-gain" : "text-loss"}`}>{sub.text}</p>
                )}
              </div>
            ))}
          </div>
          {expiryAlerts.map((r) => (
            <p key={r.id} className="text-[11px] font-medium text-orange-500 dark:text-orange-400">
              ⚠ คะแนน {r.expiring_points!.toLocaleString()} หมดอายุ {r.expiry_date}
            </p>
          ))}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Category breakdown */}
            {sortedCats.length > 0 && (
              <div className="bg-transparent rounded-2xl p-5">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
                  ยอดตามหมวดหมู่
                </h3>
                <ResponsiveContainer width="100%" height={sharedChartHeight}>
                  <BarChart
                    data={sortedCats.map(([name, { amount }]) => ({
                      name,
                      amount,
                    }))}
                    layout="vertical"
                    margin={{ left: 4, right: 16 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      type="number"
                      tickFormatter={(v) => `฿${(v / 1000).toFixed(0)}k`}
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      tick={{ fontSize: 11 }}
                      width={110}
                      tickFormatter={(v: string) =>
                        v.length > 13 ? v.slice(0, 12) + "…" : v
                      }
                    />
                    <Bar dataKey="amount" radius={[0, 6, 6, 0]}>
                      {sortedCats.map((_, i) => (
                        <Cell
                          key={i}
                          fill={PIE_COLORS[i % PIE_COLORS.length]}
                        />
                      ))}
                      <LabelList
                        dataKey="amount"
                        position="right"
                        formatter={(v: number) => `฿${fmt(v)}`}
                        style={{ fontSize: 11, fill: "#6b7280" }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Card breakdown */}
            {recs.length > 1 && (
              <div className="bg-transparent rounded-2xl p-5">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
                  ยอดตามบัตร
                </h3>
                <ResponsiveContainer width="100%" height={sharedChartHeight}>
                  <BarChart
                    data={cardChartData}
                    layout="vertical"
                    margin={{ left: 4, right: 16 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      type="number"
                      tickFormatter={(v) => `฿${(v / 1000).toFixed(0)}k`}
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      tick={{ fontSize: 11 }}
                      width={110}
                      tickFormatter={(v: string) =>
                        v.length > 13 ? v.slice(0, 12) + "…" : v
                      }
                    />
                    <Bar dataKey="amount" radius={[0, 6, 6, 0]}>
                      {cardChartData.map((_, i) => (
                        <Cell
                          key={i}
                          fill={PIE_COLORS[i % PIE_COLORS.length]}
                        />
                      ))}
                      <LabelList
                        dataKey="amount"
                        position="right"
                        formatter={(v: number) => `฿${fmt(v)}`}
                        style={{ fontSize: 11, fill: "#6b7280" }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Category filter pills */}
          {sortedCats.length > 0 && (
            <div className="px-1">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setFilterCat(null)}
                  className={`flex items-center gap-1 px-2 h-9 rounded-lg text-xs font-medium transition-all ${
                    !filterCat
                      ? "bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-900"
                      : "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"
                  }`}
                >
                  ทั้งหมด
                  <span className={`text-[10px] px-1 py-0.5 rounded ${!filterCat ? "bg-white/20 text-white" : "bg-gray-200 dark:bg-gray-700 text-gray-500"}`}>
                    {sortedCats.reduce((s, [, v]) => s + v.count, 0)}
                  </span>
                </button>
                {sortedCats.map(([cat, { amount, count }]) => {
                  const cfg = CATEGORY_CONFIG[cat] ?? CATEGORY_CONFIG["อื่นๆ"];
                  const active = filterCat === cat;
                  return (
                    <button
                      key={cat}
                      onClick={() => setFilterCat(active ? null : cat)}
                      className={`flex items-center gap-1 px-2 h-9 rounded-lg text-xs font-medium transition-all ${
                        active
                          ? "bg-brand shadow-sm"
                          : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                      }`}
                    >
                      <span className={active ? "text-white" : "text-gray-400 dark:text-gray-400"}>
                        {cfg.icon}
                      </span>
                      {cat}
                      {active && (
                        <span className="text-[10px] text-white/80 font-normal">฿{fmt(amount)}</span>
                      )}
                      {!active && (
                        <span className="text-[10px] bg-gray-200 dark:bg-gray-700 text-gray-400 px-1 py-0.5 rounded">
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Transaction list */}
          {(() => {
            const filteredTxns = txns.filter(
              (t) =>
                (!filterCard || t.card_last4 === filterCard) &&
                (!filterCat || t.category === filterCat),
            );
            const cardEmptyRec = filterCard
              ? recs.find((r) => r.card_last4 === filterCard)
              : null;
            return (
              <div className="bg-transparent rounded-2xl overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2 flex-wrap">
                    รายการใช้จ่าย
                    {filterCard && (
                      <span className="text-xs text-primary-500">
                        · ···{filterCard}
                      </span>
                    )}
                    {filterCat && (
                      <span className="text-xs text-primary-500">
                        · {filterCat}
                      </span>
                    )}
                  </h3>
                  <div className="flex items-center gap-2">
                    {(filterCard || filterCat) && (
                      <button
                        onClick={() => {
                          setFilterCard(null);
                          setFilterCat(null);
                        }}
                        className="text-xs text-gray-400 hover:text-gray-600"
                      >
                        ล้างตัวกรอง ×
                      </button>
                    )}
                    <span className="text-xs text-gray-400">
                      {filteredTxns.length} รายการ
                    </span>
                  </div>
                </div>
                {txnLoading ? (
                  <div className="px-5 py-8 text-center text-sm text-gray-400">
                    กำลังโหลด...
                  </div>
                ) : txns.length === 0 ? (
                  <div className="px-5 py-8 text-center text-sm text-gray-400">
                    ไม่มีข้อมูล — กด บันทึก หลังอัปโหลด Statement
                    เพื่อเก็บรายการ
                  </div>
                ) : filteredTxns.length === 0 ? (
                  <div className="flex flex-col items-center justify-center gap-3 py-14 px-6">
                    <div className="w-14 h-14 rounded-2xl bg-gray-100 dark:bg-gray-700/60 flex items-center justify-center">
                      <CreditCard className="w-7 h-7 text-gray-300 dark:text-gray-400" />
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                        {filterCat
                          ? `ไม่มีรายการหมวด "${filterCat}"${filterCard ? ` ของบัตร ···${filterCard}` : ""}`
                          : `บัตร ···${filterCard} ไม่มีรายการเดือนนี้`}
                      </p>
                      {cardEmptyRec && cardEmptyRec.total_points > 0 && (
                        <p className="text-xs text-gray-400 dark:text-gray-400 mt-1">
                          คะแนนคงเหลือ{" "}
                          <span className="font-semibold text-primary-500">
                            {cardEmptyRec.total_points.toLocaleString()}
                          </span>{" "}
                          คะแนน
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        setFilterCard(null);
                        setFilterCat(null);
                      }}
                      className="text-xs text-primary-500 hover:text-primary-600 font-medium mt-1"
                    >
                      ดูทุกบัตร →
                    </button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left table-head">
                          <th className="px-5 py-3">วันที่</th>
                          <th className="px-5 py-3">ร้านค้า / รายการ</th>
                          <th className="px-5 py-3">หมวดหมู่</th>
                          {recs.length > 1 && !filterCard && (
                            <th className="px-5 py-3">บัตร</th>
                          )}
                          <th className="px-5 py-3 text-right">จำนวนเงิน</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {txns
                          .filter(
                            (t) =>
                              (!filterCard || t.card_last4 === filterCard) &&
                              (!filterCat || t.category === filterCat),
                          )
                          .map((t) => {
                            const cfg =
                              CATEGORY_CONFIG[t.category] ??
                              CATEGORY_CONFIG["อื่นๆ"];
                            return (
                              <tr
                                key={t.id}
                                className="border-l-2 border-l-transparent hover:border-l-primary-400 hover:bg-primary-50/30 dark:hover:bg-primary-900/10 transition-all duration-150"
                              >
                                <td className="px-5 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap font-mono text-xs">
                                  {t.date}
                                </td>
                                <td className="px-5 py-3 text-gray-800 dark:text-gray-200 max-w-xs">
                                  <span className="truncate block">
                                    {t.merchant}
                                  </span>
                                </td>
                                <td className="px-5 py-3">
                                  <button
                                    onMouseDown={(e) => openDropdown(e, t.id)}
                                    title="คลิกเพื่อเปลี่ยนหมวดหมู่"
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-white border-0 outline-none focus:outline-none cursor-pointer"
                                    style={{ backgroundColor: cfg.color }}
                                  >
                                    <span className="flex-shrink-0 flex items-center">
                                      {cfg.icon}
                                    </span>
                                    {t.category}
                                  </button>
                                </td>
                                {recs.length > 1 && !filterCard && (
                                  <td className="px-5 py-3 whitespace-nowrap">
                                    {t.card_last4 ? (
                                      <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                                        {cardImage(
                                          t.card_type || undefined,
                                          undefined,
                                          t.card_last4,
                                          cardProfiles,
                                        ) && (
                                          <img
                                            src={
                                              cardImage(
                                                t.card_type || undefined,
                                                undefined,
                                                t.card_last4,
                                                cardProfiles,
                                              )!
                                            }
                                            className="h-4 w-auto rounded object-contain"
                                            alt=""
                                          />
                                        )}
                                        {cardDisplayName(
                                          t.card_type || undefined,
                                          undefined,
                                          t.card_last4,
                                          cardProfiles,
                                        )}{" "}
                                        ···{t.card_last4}
                                      </span>
                                    ) : (
                                      "—"
                                    )}
                                  </td>
                                )}
                                <td
                                  className={`px-5 py-3 text-right font-semibold whitespace-nowrap ${
                                    t.is_credit
                                      ? "text-gain"
                                      : "text-gray-900 dark:text-gray-100"
                                  }`}
                                >
                                  {t.is_credit ? "-" : ""}฿{fmt(t.amount)}
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Category dropdown portal */}
          {editingId !== null &&
            dropdownPos &&
            createPortal(
              <div
                ref={dropdownRef}
                className="fixed z-[9999] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl py-1 min-w-[180px]"
                style={{ top: dropdownPos.top, left: dropdownPos.left }}
              >
                {Object.entries(CATEGORY_CONFIG).map(([cat, catCfg]) => {
                  const currentCat = txns.find(
                    (t) => t.id === editingId,
                  )?.category;
                  return (
                    <button
                      key={cat}
                      onClick={() => updateCategory(editingId, cat)}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${cat === currentCat ? "font-semibold" : ""}`}
                    >
                      <span
                        className="w-5 h-5 rounded-full flex items-center justify-center text-white flex-shrink-0"
                        style={{ backgroundColor: catCfg.color }}
                      >
                        {catCfg.icon}
                      </span>
                      <span className="text-gray-700 dark:text-gray-300">
                        {cat}
                      </span>
                      {cat === currentCat && (
                        <span className="ml-auto text-gray-400">✓</span>
                      )}
                    </button>
                  );
                })}
              </div>,
              document.body,
            )}
        </>
      )}
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const SYNC_YEARS = (() => {
  const cur = new Date().getFullYear()
  return [cur - 2, cur - 1, cur]
})()

const SyncYearPicker: React.FC<{ onStart: (after: string, before: string) => void }> = ({ onStart }) => {
  const cur = new Date().getFullYear()
  const [fromYear, setFromYear] = React.useState(cur)
  const [toYear, setToYear] = React.useState(cur)

  const handleStart = () => {
    const lo = Math.min(fromYear, toYear)
    const hi = Math.max(fromYear, toYear)
    onStart(`${lo}/01/01`, `${hi + 1}/01/01`)
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 dark:text-gray-400">เลือกช่วงปีที่ต้องการ Sync — ระบบจะดึง PDF จาก Gmail และบันทึกอัตโนมัติ ข้ามรายการซ้ำ</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5 font-medium">จากปี</p>
          <div className="flex flex-col gap-1.5">
            {SYNC_YEARS.map(y => (
              <button key={y} onClick={() => setFromYear(y)}
                className={`w-full py-2 rounded-xl text-sm font-semibold border-2 transition-all ${fromYear === y ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400' : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-blue-300'}`}>
                {y}
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5 font-medium">ถึงปี</p>
          <div className="flex flex-col gap-1.5">
            {SYNC_YEARS.map(y => (
              <button key={y} onClick={() => setToYear(y)}
                className={`w-full py-2 rounded-xl text-sm font-semibold border-2 transition-all ${toYear === y ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400' : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-blue-300'}`}>
                {y}
              </button>
            ))}
          </div>
        </div>
      </div>
      <button onClick={handleStart}
        className={buttonClass('primary', 'md', 'w-full')}>
        <Download className="w-4 h-4" />
        เริ่ม Sync {Math.min(fromYear, toYear) === Math.max(fromYear, toYear) ? fromYear : `${Math.min(fromYear, toYear)}–${Math.max(fromYear, toYear)}`}
      </button>
    </div>
  )
}

interface GmailFailedLogEntry {
  message_id: string
  file: string
  subject: string
  email_date: string | null
  error: string
  first_seen: string
  last_attempt: string
  attempts: number
}

const fmtLogDate = (v: string | null) => {
  if (!v) return ''
  const d = new Date(v)
  return isNaN(d.getTime()) ? v : d.toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit' })
}

// Persistent log of PDFs that failed to parse in past Gmail syncs (server removes entries once they succeed)
const GmailFailedLog: React.FC<{ refreshKey: number; standalone?: boolean }> = ({ refreshKey, standalone = false }) => {
  const [entries, setEntries] = React.useState<GmailFailedLogEntry[] | null>(null)
  const [open, setOpen] = React.useState(standalone)
  const [clearing, setClearing] = React.useState(false)

  React.useEffect(() => {
    apiFetch('/api/gmail/sync-log')
      .then((r) => (r.ok ? r.json() : { entries: [] }))
      .then((d) => setEntries(d.entries ?? []))
      .catch(() => setEntries([]))
  }, [refreshKey])

  if (entries === null) {
    return standalone ? <p className="text-xs text-gray-400 py-4 text-center">กำลังโหลด...</p> : null
  }
  if (entries.length === 0) {
    return standalone ? (
      <p className="flex items-center justify-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 py-6">
        <CheckCircle2 className="w-3.5 h-3.5 text-gain" />
        ไม่มีไฟล์ที่อ่านไม่ได้
      </p>
    ) : null
  }

  const clearLog = async () => {
    setClearing(true)
    try {
      const r = await apiFetch('/api/gmail/sync-log', { method: 'DELETE' })
      if (!r.ok) throw new Error()
      setEntries([])
      toast.success('ล้าง log แล้ว')
    } catch {
      toast.error('ล้าง log ไม่สำเร็จ')
    } finally {
      setClearing(false)
    }
  }

  return (
    <div className={standalone ? "" : "rounded-xl border border-amber-200 dark:border-amber-900/50 overflow-hidden"}>
      {!standalone && <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-3 py-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 hover:bg-amber-100/70 dark:hover:bg-amber-950/50 transition-colors"
      >
        <AlertCircle className="w-3.5 h-3.5 shrink-0" />
        <span className="font-medium flex-1 text-left">Log ไฟล์ที่อ่านไม่ได้ ({entries.length})</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>}
      {open && (
        <div className={standalone ? "space-y-3" : "p-2.5 space-y-2"}>
          <div className={`${standalone ? "max-h-[60vh]" : "max-h-48"} overflow-y-auto space-y-1.5`}>
            {entries.map((e) => (
              <div key={`${e.message_id}|${e.file}`} className="text-xs border-b border-gray-100 dark:border-gray-700 pb-1.5 last:border-0 last:pb-0">
                <div className="flex items-start gap-2">
                  <p className="font-medium text-gray-800 dark:text-gray-200 truncate flex-1">{e.file}</p>
                  {e.email_date && <span className="text-gray-400 shrink-0">{fmtLogDate(e.email_date)}</span>}
                </div>
                <p className="text-gray-500 dark:text-gray-400 truncate mt-0.5">{e.subject}</p>
                <p className="text-amber-600 dark:text-amber-400 break-words">{e.error}</p>
                <p className="text-[10px] text-gray-400 mt-0.5">
                  ลองล่าสุด {fmtLogDate(e.last_attempt)}{e.attempts > 1 ? ` · ${e.attempts} ครั้ง` : ''}
                </p>
              </div>
            ))}
          </div>
          <button
            onClick={clearLog}
            disabled={clearing}
            className="flex items-center gap-1 text-xs text-gray-400 hover:text-loss transition-colors disabled:opacity-50"
          >
            <Trash2 className="w-3 h-3" />
            {clearing ? 'กำลังล้าง...' : 'ล้าง log'}
          </button>
        </div>
      )}
    </div>
  )
}

const CreditPage: React.FC = () => {
  const [showUpload, setShowUpload] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [history, setHistory] = useState<StatementRecord[]>([]);
  const [clearing, setClearing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const { getConfigValue, updateConfig } = useConfigStore();

  const loadHistory = async () => {
    try {
      const res = await apiFetch("/api/credit-statements?months=120");
      if (res.ok) setHistory(await res.json());
    } catch {}
  };

  const closeModal = () => {
    setShowUpload(false);
    setAutoSaving(false);
  };

  const autoSaveStatement = async (
    result: ParseResult,
    file?: File,
    passwords?: string,
  ) => {
    // Duplicate detection
    if (result.statement_month) {
      const incomingLast4s = Array.from(
        new Set(
          result.transactions
            .filter((t) => t.cardLast4)
            .map((t) => t.cardLast4!),
        ),
      );
      const duplicates = history.filter(
        (r) =>
          r.statement_month === result.statement_month &&
          r.bank === result.bank &&
          incomingLast4s.includes(r.card_last4),
      );
      if (duplicates.length > 0) {
        const labels = duplicates.map((r) => `*${r.card_last4}`).join(", ");
        const ok = window.confirm(
          `พบข้อมูลซ้ำ!\n\nบัตร ${labels} มีข้อมูลเดือน ${result.statement_month} อยู่แล้ว\n\nต้องการบันทึกทับข้อมูลเดิมหรือไม่?`,
        );
        if (!ok) { setAutoSaving(false); return; }
      }
    }

    const chargeCount = result.transactions.filter((t) => !t.isCredit).length;
    toast.success(chargeCount > 0 ? `พบ ${chargeCount} รายการ` : "ไม่มีรายการใช้จ่ายในรอบนี้");
    setAutoSaving(true);
    try {
      const res = await apiFetch("/api/credit-statements", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          bank: result.bank,
          period: result.period,
          transactions: result.transactions,
          points_by_card: result.points_by_card ?? {},
          points_expiry_by_card: result.points_expiry_by_card ?? {},
          earned_points_by_card: result.earned_points_by_card ?? {},
          card_sections: result.card_sections ?? {},
          card_meta: result.card_meta ?? [],
        }),
      });
      if (!res.ok) throw new Error();
      toast.success("บันทึกแล้ว");
      await loadHistory();
      closeModal();

      // Attach the original PDF for later verification (best-effort, non-blocking)
      if (file && result.statement_month) {
        try {
          const pdfForm = new FormData();
          pdfForm.append("file", file);
          pdfForm.append("bank", result.bank);
          pdfForm.append("statement_month", result.statement_month);
          if (passwords) pdfForm.append("passwords", passwords);
          const pdfRes = await apiFetch("/api/credit-statements/pdf", {
            method: "POST",
            body: pdfForm,
          });
          if (pdfRes.ok) await loadHistory();
        } catch {
          /* soft-fail — the statement data is already saved */
        }
      }

      // Auto-save due dates detected from PDF
      if (result.cut_day != null || result.due_day != null) {
        let cardProfiles: CardProfiles = {};
        try {
          cardProfiles = JSON.parse(
            getConfigValue("credit_card_profiles", "{}"),
          );
        } catch {
          /**/
        }
        const uniqueLast4s = Array.from(
          new Set(
            result.transactions
              .filter((t) => t.cardLast4)
              .map((t) => t.cardLast4!),
          ),
        );
        if (uniqueLast4s.length > 0) {
          let dueDates: DueDateMap = {};
          try {
            dueDates = JSON.parse(getConfigValue("credit_due_dates", "{}"));
          } catch {
            /**/
          }
          for (const last4 of uniqueLast4s) {
            const existing = dueDates[last4];
            const tx = result.transactions.find((t) => t.cardLast4 === last4);
            dueDates[last4] = {
              card_name:
                existing?.card_name ||
                cardDisplayName(tx?.cardType, result.bank, last4, cardProfiles),
              cut_day: result.cut_day ?? existing?.cut_day ?? 25,
              due_day: result.due_day ?? existing?.due_day ?? 15,
            };
          }
          try {
            await updateConfig(
              "credit_due_dates",
              JSON.stringify(dueDates),
              "Credit card due dates",
            );
            const parts = [
              result.cut_day != null ? `ตัดวันที่ ${result.cut_day}` : "",
              result.due_day != null ? `ชำระวันที่ ${result.due_day}` : "",
            ].filter(Boolean);
            toast(`📅 บันทึกวันชำระอัตโนมัติ: ${parts.join(" · ")}`, {
              duration: 4000,
            });
          } catch {
            /**/
          }
        }
      }
    } catch {
      toast.error("บันทึกไม่สำเร็จ");
      setAutoSaving(false);
    }
  };

  const syncCategories = async () => {
    setSyncing(true);
    try {
      const res = await apiFetch("/api/credit-transactions/sync-categories", {
        method: "POST",
      });
      if (res.ok) {
        const { updated } = await res.json();
        toast.success(
          updated > 0
            ? `อัปเดตหมวดหมู่ ${updated} รายการ`
            : "ไม่มีรายการที่ต้องอัปเดต",
        );
        if (updated > 0) await loadHistory();
      } else {
        toast.error("Sync ไม่สำเร็จ");
      }
    } catch {
      toast.error("เกิดข้อผิดพลาด");
    } finally {
      setSyncing(false);
    }
  };

  const clearAll = async () => {
    if (!window.confirm("ล้างข้อมูลบัตรเครดิตทั้งหมด?")) return;
    setClearing(true);
    try {
      const res = await apiFetch("/api/credit-statements/all", {
        method: "DELETE",
      });
      if (res.ok) {
        setHistory([]);
        toast.success("ล้างข้อมูลแล้ว");
      } else toast.error("ล้างไม่สำเร็จ");
    } catch {
      toast.error("เกิดข้อผิดพลาด");
    } finally {
      setClearing(false);
    }
  };

  React.useEffect(() => {
    loadHistory();
  }, []);

  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeModal();
    };
    if (showUpload) window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [showUpload]);

  const hasHistory = history.length > 0;
  const [activeView, setActiveView] = useState<
    "monthly" | "overview" | "search"
  >("monthly");

  let cardProfiles: CardProfiles = {};
  try {
    cardProfiles = JSON.parse(getConfigValue("credit_card_profiles", "{}"));
  } catch {}
  const uniqueCardsFromHistory = Array.from(
    new Map(
      history
        .filter((r) => r.card_last4)
        .map((r) => [
          r.card_last4,
          {
            last4: r.card_last4,
            bank: r.bank || "",
            cardType: r.card_type || "",
          },
        ]),
    ).values(),
  );
  const [gmailConnected, setGmailConnected] = React.useState<boolean | null>(
    null,
  );
  const [showHistoryModal, setShowHistoryModal] = React.useState(false);
  const [historyImporting, setHistoryImporting] = React.useState(false);
  const [historyAbort, setHistoryAbort] = React.useState(false);
  const historyAbortRef = React.useRef(false);
  const [historyStats, setHistoryStats] = React.useState<{
    batch: number;
    found: number;
    saved: number;
    skipped: number;
    errors: { file: string; subject: string; error: string }[];
    done: boolean;
  }>({
    batch: 0,
    found: 0,
    saved: 0,
    skipped: 0,
    errors: [],
    done: false,
  });
  const [showPwdModal, setShowPwdModal] = React.useState(false);
  const [showFailedLog, setShowFailedLog] = React.useState(false);
  const [savedPasswords, setSavedPasswords] = React.useState<string[]>([]);
  const [newPwd, setNewPwd] = React.useState("");
  const [pwdSaving, setPwdSaving] = React.useState(false);
  const [revealIdx, setRevealIdx] = React.useState<number | null>(null);

  React.useEffect(() => {
    apiFetch("/api/gmail/status")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setGmailConnected(d?.connected ?? false))
      .catch(() => setGmailConnected(false));
  }, []);

  const openPwdModal = async () => {
    setShowPwdModal(true);
    setRevealIdx(null);
    try {
      const r = await apiFetch("/api/gmail/passwords");
      const d = await r.json();
      setSavedPasswords(d.passwords ?? []);
    } catch {
      setSavedPasswords([]);
    }
  };

  const savePwdList = async (list: string[]) => {
    setPwdSaving(true);
    try {
      await apiFetch("/api/gmail/passwords", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ passwords: list }),
      });
      setSavedPasswords(list);
      toast.success("บันทึก password เรียบร้อย");
    } catch {
      toast.error("บันทึกไม่สำเร็จ");
    } finally {
      setPwdSaving(false);
    }
  };

  const addPassword = () => {
    const trimmed = newPwd.trim();
    if (!trimmed) return;
    if (savedPasswords.includes(trimmed)) {
      toast("Password นี้มีอยู่แล้ว", { icon: "ℹ️" });
      return;
    }
    const next = [...savedPasswords, trimmed];
    setSavedPasswords(next);
    setNewPwd("");
    savePwdList(next);
  };

  const removePassword = (idx: number) => {
    const next = savedPasswords.filter((_, i) => i !== idx);
    setSavedPasswords(next);
    savePwdList(next);
  };

  const startGmailSync = async (after: string, before: string) => {
    setHistoryImporting(true);
    historyAbortRef.current = false;
    setHistoryAbort(false);
    setHistoryStats({ batch: 0, found: 0, saved: 0, skipped: 0, errors: [], done: false });

    let pageToken: string | undefined;
    let batch = 0;
    let totalFound = 0, totalSaved = 0, totalSkipped = 0;
    const allErrors: { file: string; subject: string; error: string }[] = [];

    try {
      while (true) {
        if (historyAbortRef.current) break;
        batch++;
        const body: Record<string, unknown> = { after, before };
        if (pageToken) body.pageToken = pageToken;

        const res = await apiFetch("/api/gmail/sync", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok) { toast.error(data.error || "Sync ไม่สำเร็จ"); break; }

        totalFound += data.found ?? 0;
        totalSaved += data.saved ?? 0;
        totalSkipped += data.skipped ?? 0;
        (data.errors ?? []).forEach((e: { file: string; subject: string; error: string }) => allErrors.push(e));
        pageToken = data.nextPageToken;

        setHistoryStats({ batch, found: totalFound, saved: totalSaved, skipped: totalSkipped, errors: [...allErrors], done: !pageToken });
        if (!pageToken) break;
      }
    } catch {
      toast.error("เกิดข้อผิดพลาด");
    } finally {
      setHistoryImporting(false);
      if (totalSaved > 0) loadHistory();
    }
  };


  return (
    <motion.div variants={pageContainerVariants} initial="hidden" animate="visible">
    <motion.div variants={pageItemVariants} className="card  overflow-hidden">
      {/* Tab bar — top */}
      <div className="flex border-b border-gray-100 dark:border-gray-700/50">
        {(
          [
            { v: "monthly", label: "รายเดือน", icon: CalendarDays },
            { v: "overview", label: "ภาพรวม", icon: LayoutDashboard },
            { v: "search", label: "ค้นหา", icon: Search },
          ] as const
        ).map(({ v, label, icon: Icon }) => (
          <button
            key={v}
            onClick={() => setActiveView(v)}
            className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeView === v
                ? "border-primary-500 text-primary-600 dark:text-primary-400"
                : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      <div className="p-6 space-y-6">
        {/* Header — Gmail group · more menu · main action */}
        <div className="flex items-center justify-end gap-2">
          {gmailConnected && (
            <div className="btn-group">
              <Button variant="secondary"
                onClick={() => { setShowHistoryModal(true); setHistoryStats({ batch: 0, found: 0, saved: 0, skipped: 0, errors: [], done: false }); }}
                title="Sync statement จาก Gmail"
              >
                <Download className="w-4 h-4" />
                Sync Gmail
              </Button>
              <Button variant="secondary" size="icon"
                onClick={openPwdModal}
                title="จัดการ Password PDF"
                aria-label="จัดการ Password PDF"
              >
                <Lock className="w-4 h-4" />
              </Button>
            </div>
          )}
          {(hasHistory || gmailConnected) && (
            <ActionMenu
              label="เมนูเพิ่มเติม"
              items={[
                ...(gmailConnected
                  ? [{
                      label: "Log ไฟล์ที่อ่านไม่ได้",
                      icon: <AlertCircle className="w-4 h-4" />,
                      onClick: () => setShowFailedLog(true),
                    }]
                  : []),
                ...(hasHistory ? [{
                  label: syncing ? "กำลัง Sync หมวดหมู่..." : "Sync หมวดหมู่",
                  icon: <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />,
                  onClick: syncCategories,
                  disabled: syncing,
                  separated: !!gmailConnected,
                },
                {
                  label: clearing ? "กำลังล้างข้อมูล..." : "ล้างข้อมูลทั้งหมด",
                  icon: <Trash2 className="w-4 h-4" />,
                  onClick: clearAll,
                  disabled: clearing,
                  danger: true,
                  separated: true,
                }] : []),
              ]}
            />
          )}
          <Button onClick={() => setShowUpload(true)}>
            <Upload className="w-4 h-4" />
            อัปโหลด Statement
          </Button>
        </div>

        {/* Due Date Section — hidden until UI is ready */}
        {/* <DueDateSection cards={uniqueCardsFromHistory} profiles={cardProfiles} /> */}

        {/* History dashboard */}
        {hasHistory ? (
          <HistoryDashboard
            history={history}
            onRefresh={loadHistory}
            activeView={activeView}
          />
        ) : (
          <div className="min-h-64" />
        )}
      </div>
      {/* end p-6 space-y-6 */}

      {/* Analyzing overlay */}
      {createPortal(
        autoSaving ? (
          <div className="fixed inset-0 z-[9997] flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-4 bg-white dark:bg-gray-800 rounded-2xl px-10 py-8 shadow-2xl">
              <div className="w-10 h-10 border-[3px] border-primary-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                กำลังวิเคราะห์และบันทึก...
              </p>
            </div>
          </div>
        ) : null,
        document.body,
      )}

      {/* Upload Modal — rendered via portal to escape parent transforms */}
      {createPortal(
        <AnimatePresence>
          {showUpload && (
            <>
              {/* Backdrop */}
              <motion.div
                key="backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={closeModal}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]"
              />

              {/* Modal container — scrollable */}
              <div className="sheet fixed inset-0 z-[9999] overflow-y-auto flex items-center justify-center p-4">
                <motion.div
                  key="modal"
                  initial={{ opacity: 0, scale: 0.96, y: 16 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96, y: 16 }}
                  transition={{ type: "spring", damping: 28, stiffness: 320 }}
                  className="w-full max-w-lg min-h-[420px] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 overflow-hidden"
                >
                  {/* Modal header */}
                  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                    <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                      <Upload className="w-4 h-4 text-primary-500" />
                      อัปโหลด Statement
                    </h2>
                    <button
                      onClick={closeModal}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Modal body */}
                  <div className="p-6">
                    <UploadZone
                      onResult={autoSaveStatement}
                      onStartAnalyzing={() => {
                        setShowUpload(false);
                        setAutoSaving(true);
                      }}
                    />
                  </div>
                </motion.div>
              </div>
            </>
          )}
        </AnimatePresence>,
        document.body,
      )}

      {/* Historical Import Modal */}
      {createPortal(
        <AnimatePresence>
          {showHistoryModal && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[9998] bg-black/50 backdrop-blur-sm"
                onClick={() => !historyImporting && setShowHistoryModal(false)}
              />
              <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 pointer-events-none">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.18 }}
                className="w-full max-w-md pointer-events-auto"
              >
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 p-6 space-y-5">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Download className="w-4 h-4 text-blue-500" />
                      <h3 className="font-semibold text-gray-900 dark:text-white text-sm">Sync Gmail</h3>
                    </div>
                    {!historyImporting && (
                      <button onClick={() => { setShowHistoryModal(false); setHistoryStats({ batch: 0, found: 0, saved: 0, skipped: 0, errors: [], done: false }); }} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {!historyImporting && !historyStats.done ? (
                    <div className="space-y-4">
                      <SyncYearPicker onStart={startGmailSync} />
                      <GmailFailedLog refreshKey={0} />
                    </div>
                  ) : historyImporting ? (
                    /* Progress view */
                    <div className="space-y-5">
                      <style>{`@keyframes gmailSweep{0%{transform:translateX(-100%)}100%{transform:translateX(450%)}}`}</style>

                      {/* Indeterminate bar */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                          <span className="flex items-center gap-1.5">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                            กำลัง sync...
                          </span>
                          <span>Batch {historyStats.batch}</span>
                        </div>
                        <div className="relative h-2 rounded-full bg-gray-100 dark:bg-gray-700/60 overflow-hidden">
                          <div
                            className="absolute inset-y-0 w-2/5 rounded-full bg-gradient-to-r from-blue-500 to-purple-600"
                            style={{ animation: 'gmailSweep 1.4s ease-in-out infinite' }}
                          />
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { label: "พบ email", val: historyStats.found, bg: "bg-gray-50 dark:bg-gray-700/40", num: "text-gray-800 dark:text-gray-200" },
                          { label: "บันทึก", val: historyStats.saved, bg: "bg-green-50 dark:bg-green-900/20", num: "text-gain" },
                          { label: "ข้ามซ้ำ", val: historyStats.skipped, bg: "bg-blue-50 dark:bg-blue-900/20", num: "text-blue-500 dark:text-blue-400" },
                        ].map(({ label, val, bg, num }) => (
                          <div key={label} className={`${bg} rounded-2xl py-3 px-1 text-center`}>
                            <p className={`text-2xl font-bold tabular-nums ${num}`}>{val}</p>
                            <p className="text-[11px] text-gray-400 mt-0.5">{label}</p>
                          </div>
                        ))}
                      </div>

                      <button
                        onClick={() => { historyAbortRef.current = true; setHistoryAbort(true); }}
                        disabled={historyAbort}
                        className="w-full py-2 text-sm rounded-xl border border-red-200 dark:border-red-800 text-loss hover:bg-red-50 dark:hover:bg-red-950 transition-colors disabled:opacity-50"
                      >
                        {historyAbort ? "กำลังหยุด..." : "หยุด"}
                      </button>
                    </div>
                  ) : (
                    /* Done view */
                    <div className="space-y-5">
                      {/* Completed bar */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-xs">
                          <span className="flex items-center gap-1.5 text-gain font-medium">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            เสร็จสมบูรณ์
                          </span>
                          <span className="text-gray-400">{historyStats.batch} batch</span>
                        </div>
                        <div className="relative h-2 rounded-full bg-gray-100 dark:bg-gray-700/60 overflow-hidden">
                          <motion.div
                            className="absolute inset-y-0 left-0 rounded-full bg-gain-gradient"
                            initial={{ width: '30%' }}
                            animate={{ width: '100%' }}
                            transition={{ duration: 0.5, ease: 'easeOut' }}
                          />
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { label: "พบ email", val: historyStats.found, bg: "bg-gray-50 dark:bg-gray-700/40", num: "text-gray-800 dark:text-gray-200" },
                          { label: "บันทึกใหม่", val: historyStats.saved, bg: "bg-green-50 dark:bg-green-900/20", num: "text-gain" },
                          { label: "ข้ามซ้ำ", val: historyStats.skipped, bg: "bg-blue-50 dark:bg-blue-900/20", num: "text-blue-500 dark:text-blue-400" },
                        ].map(({ label, val, bg, num }) => (
                          <div key={label} className={`${bg} rounded-2xl py-3 px-1 text-center`}>
                            <p className={`text-2xl font-bold tabular-nums ${num}`}>{val}</p>
                            <p className="text-[11px] text-gray-400 mt-0.5">{label}</p>
                          </div>
                        ))}
                      </div>

                      {historyStats.errors.length > 0 && (
                        <p className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                          รอบนี้มี {historyStats.errors.length} ไฟล์ที่อ่านไม่ได้ — บันทึกลง log แล้ว
                        </p>
                      )}
                      <GmailFailedLog refreshKey={historyStats.batch} />

                      <button
                        onClick={() => { setShowHistoryModal(false); setHistoryStats({ batch: 0, found: 0, saved: 0, skipped: 0, errors: [], done: false }); }}
                        className={buttonClass('primary', 'md', 'w-full')}
                      >
                        ปิด
                      </button>
                    </div>
                  )}
                </div>
              </motion.div>
              </div>
            </>
          )}
        </AnimatePresence>,
        document.body,
      )}

      {/* Gmail failed-file log modal */}
      {createPortal(
        <AnimatePresence>
          {showFailedLog && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[9998] bg-black/50 backdrop-blur-sm"
                onClick={() => setShowFailedLog(false)}
              />
              <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 pointer-events-none">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.18 }}
                className="w-full max-w-md pointer-events-auto"
              >
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-500" />
                      <h3 className="font-semibold text-gray-900 dark:text-white text-sm">Log ไฟล์ที่อ่านไม่ได้</h3>
                    </div>
                    <button
                      onClick={() => setShowFailedLog(false)}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <GmailFailedLog refreshKey={0} standalone />
                </div>
              </motion.div>
              </div>
            </>
          )}
        </AnimatePresence>,
        document.body,
      )}

      {/* PDF Password Manager Modal */}
      {createPortal(
        <AnimatePresence>
          {showPwdModal && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[9998] bg-black/50 backdrop-blur-sm"
                onClick={() => setShowPwdModal(false)}
              />
              <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 pointer-events-none">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.18 }}
                className="w-full max-w-sm pointer-events-auto"
              >
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 p-6 space-y-4">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Lock className="w-4 h-4 text-blue-500" />
                      <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
                        Password PDF (Auto-Sync)
                      </h3>
                    </div>
                    <button
                      onClick={() => setShowPwdModal(false)}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Password เหล่านี้จะถูกใช้อัตโนมัติเมื่อ Sync Gmail — ลอง try ทีละตัวจนกว่าจะเปิด PDF ได้
                  </p>

                  {/* Password list */}
                  <div className="space-y-2 min-h-[40px]">
                    {savedPasswords.length === 0 && (
                      <p className="text-xs text-gray-400 text-center py-2">
                        ยังไม่มี password — เพิ่มด้านล่าง
                      </p>
                    )}
                    {savedPasswords.map((pwd, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 bg-gray-50 dark:bg-gray-700/50 rounded-xl px-3 py-2"
                      >
                        <span className="flex-1 font-mono text-sm text-gray-700 dark:text-gray-300 select-all">
                          {revealIdx === idx ? pwd : "•".repeat(Math.min(pwd.length, 10))}
                        </span>
                        <button
                          onClick={() => setRevealIdx(revealIdx === idx ? null : idx)}
                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 shrink-0"
                        >
                          {revealIdx === idx ? (
                            <EyeOff className="w-3.5 h-3.5" />
                          ) : (
                            <Eye className="w-3.5 h-3.5" />
                          )}
                        </button>
                        <button
                          onClick={() => removePassword(idx)}
                          disabled={pwdSaving}
                          className="text-red-400 hover:text-red-600 shrink-0 disabled:opacity-50"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Add new password */}
                  <div className="flex gap-2">
                    <input
                      type="password"
                      value={newPwd}
                      onChange={(e) => setNewPwd(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && addPassword()}
                      placeholder="เพิ่ม password ใหม่"
                      className="flex-1 text-sm rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <button
                      onClick={addPassword}
                      disabled={!newPwd.trim() || pwdSaving}
                      className={buttonClass('primary', 'md')}
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
              </div>
            </>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </motion.div>
    </motion.div>
  );
};

export default CreditPage;
